import { Controller } from 'stimulus';
import axios from 'axios';

// Components
//
import Floorplan from '../../components/floorplan';

// Utils
//
import WizardValidator from '../../utils/wizard_validator';

export default class extends Controller {
  static targets = [
    'nextButton',
    'backButton',
    'saveButton',
    'form',
    'locationIdField',
    'xField',
    'yField',
    'catalogueItemIdField',
    'locationSelect',
    'transitLocationSelect',
    'storeLocationSelect',
    'childLocationSelect',
    'categorySelect',
    'locationActionSelect',
    'relocateLocationSelect',
    'relocateStoreLocationSelect',
    'relocateChildLocationSelect',
    'intransitLocationSelect',
    'intransitChildLocationSelect',
    'imageField',
    'mainImagePreview',
    'singlePlacementNotice',
    'bulkPlacementNotice',
    'bulkPlacementTotal',
    'bulkPlacementProgress',
  ];

  // Searchable dropdowns (cusotm)..............

  ensureSearchableDropdown(selectEl, { placeholder = null } = {}) {
    if (!selectEl || selectEl.tagName !== 'SELECT') return;
    if (selectEl.dataset.sdInit === 'true') {
      this.syncSearchableDropdown(selectEl, { placeholder });
      return;
    }

    selectEl.dataset.sdInit = 'true';
    selectEl.style.display = 'none';

    const root = document.createElement('div');
    root.className = 'sd-root';
    root.style.position = 'relative';

    // Single visible field: shows selection when closed; same field filters when open (no inner search box).
    const trigger = document.createElement('input');
    console.log("TRIGGER:::::", trigger);
    trigger.type = 'text';
    trigger.className = selectEl.className || 'form-control select';
    trigger.autocomplete = 'off';
    trigger.setAttribute('inputmode', 'search');
    trigger.style.display = 'block';
    trigger.style.width = '100%';
    trigger.style.textAlign = 'left';
    trigger.readOnly = true;
    console.log("TRIGGER:::::", trigger);

    const panel = document.createElement('div');
    panel.className = 'sd-panel';
    panel.style.position = 'absolute';
    panel.style.left = '0';
    panel.style.right = '0';
    panel.style.top = '100%';
    panel.style.zIndex = '9999';
    panel.style.background = '#fff';
    panel.style.border = '1px solid rgba(0,0,0,0.15)';
    panel.style.borderTop = '0';
    panel.style.maxHeight = '260px';
    panel.style.overflow = 'auto';
    panel.style.display = 'none';

    const list = document.createElement('div');
    list.className = 'sd-list';
    panel.appendChild(list);

    selectEl.insertAdjacentElement('afterend', root);
    root.appendChild(trigger);
    root.appendChild(panel);

    const phDefault =
      placeholder ||
      selectEl.options?.[0]?.textContent ||
      'Select…';
    selectEl._sdConfigPlaceholder = phDefault;

    const close = () => {
      panel.style.display = 'none';
      root.dataset.open = 'false';
      trigger.readOnly = true;
      trigger.placeholder = phDefault;
      this.updateSearchableDropdownLabel(selectEl, { placeholder: phDefault });
      this.filterSearchableDropdown(selectEl, '');
      if (this._sdActiveSelect === selectEl) {
        this._sdActiveSelect = null;
      }
    };

    const open = () => {
      this.closeOtherSearchableDropdowns(selectEl);
      this._sdActiveSelect = selectEl;
      panel.style.display = 'block';
      root.dataset.open = 'true';
      trigger.readOnly = false;
      trigger.value = '';
      trigger.placeholder = 'Type to search…';
      this.filterSearchableDropdown(selectEl, '');
      trigger.focus();
    };

    selectEl._sd = { root, trigger, panel, list, close, open };

    trigger.addEventListener('mousedown', (e) => {
      e.stopPropagation();
      this._sdActiveSelect = selectEl;
      if (root.dataset.open === 'true') return;
      if (!trigger.readOnly) return;
      open();
    });

    trigger.addEventListener('focus', () => {
      this._sdActiveSelect = selectEl;
    });

    trigger.addEventListener('input', (e) => {
      e.stopPropagation();
      if (root.dataset.open !== 'true') return;
      this.filterSearchableDropdown(selectEl, trigger.value);
    });

    list.addEventListener('mousedown', (e) => {
      e.stopPropagation();
      this._sdActiveSelect = selectEl;
    });

    // Close on click-outside + escape
    if (!this._sdDocBound) {
      document.addEventListener('click', (e) => {
        const active = this._sdActiveSelect;
        if (!active || !active._sd) return;
        if (active._sd.root.contains(e.target)) return;
        active._sd.close();
        this._sdActiveSelect = null;
      });
      document.addEventListener('keydown', (e) => {
        const active = this._sdActiveSelect;
        if (!active || !active._sd) return;
        if (e.key === 'Escape') {
          active._sd.close();
          this._sdActiveSelect = null;
        }
      });
      this._sdDocBound = true;
    }

    selectEl.addEventListener('change', () => {
      this.updateSearchableDropdownLabel(selectEl, { placeholder: selectEl._sdConfigPlaceholder });
    });

    this.syncSearchableDropdown(selectEl, { placeholder: phDefault });
  }

  syncSearchableDropdown(selectEl, { placeholder = null } = {}) {
    if (!selectEl || !selectEl._sd) return;
    const { list } = selectEl._sd;
    while (list.firstChild) list.removeChild(list.firstChild);

    const ph =
      placeholder ||
      selectEl.options?.[0]?.textContent ||
      'Select…';

    const opts = Array.from(selectEl.options || []);
    const rendered = opts
      .filter((o, idx) => !(idx === 0 && o.value === ''))
      .map((o) => ({
        value: o.value,
        text: o.textContent || '',
      }));

    selectEl._sdAll = rendered;

    rendered.forEach((o) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'sd-option';
      row.dataset.value = o.value;
      row.textContent = o.text;
      row.style.display = 'block';
      row.style.width = '100%';
      row.style.textAlign = 'left';
      row.style.padding = '0.5rem 0.75rem';
      row.style.border = '0';
      row.style.background = 'transparent';
      row.addEventListener('click', () => {
        selectEl.value = o.value;
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
        selectEl._sd.close();
      });
      list.appendChild(row);
    });

    this.updateSearchableDropdownLabel(selectEl, { placeholder: ph });
  }

  filterSearchableDropdown(selectEl, queryRaw) {
    if (!selectEl || !selectEl._sd) return;
    const q = String(queryRaw || '').trim().toLowerCase();
    const rows = Array.from(selectEl._sd.list.querySelectorAll('.sd-option'));
    rows.forEach((row) => {
      const text = (row.textContent || '').toLowerCase();
      row.style.display = !q || text.includes(q) ? 'block' : 'none';
    });
  }

  updateSearchableDropdownLabel(selectEl, { placeholder = null } = {}) {
    if (!selectEl || !selectEl._sd) return;
    const { trigger } = selectEl._sd;
    const selectedText = selectEl.selectedOptions?.[0]?.textContent || '';
    const ph =
      placeholder ||
      selectEl.options?.[0]?.textContent ||
      'Select…';
    if (selectEl.value) {
      trigger.value = selectedText;
    } else {
      trigger.value = '';
    }
    trigger.placeholder = ph;
  }

  destroySearchableDropdown(selectEl) {
    if (!selectEl || !selectEl._sd) return;
    if (this._sdActiveSelect === selectEl) {
      this._sdActiveSelect = null;
    }
    try {
      selectEl._sd.root.remove();
    } catch (e) {}
    selectEl.style.display = '';
    delete selectEl._sd;
    delete selectEl._sdAll;
    delete selectEl.dataset.sdInit;
    delete selectEl._sdConfigPlaceholder;
  }

  /** Close every other open searchable dropdown in this wizard (keeps one open at a time). */
  closeOtherSearchableDropdowns(exceptSelectEl) {
    if (!this.element) return;
    this.element.querySelectorAll('select[data-sd-init="true"]').forEach((sel) => {
      if (sel === exceptSelectEl || !sel._sd) return;
      const root = sel._sd.root;
      if (root && root.dataset.open === 'true') {
        sel._sd.close();
      }
    });
  }
  // -------------------------------------------------------------

  connect() {
    // Detect edit mode from form data attribute
    const isEditMode = this.formTarget.dataset.isEdit === 'true';
    
    this.currentStep = 1;
    this.isEditMode = isEditMode;
    // Create: details (1) → location & position (2) → review (3). Edit: same three steps.
    this.maxStep = 3;

    this.selectedLocation = null;
    this.floorplanInstance = null;
    this.fixedAttributes = [];
    this.variableAttributes = [];
    this.preparedAttributes = [];
    this.lastCatalogueItemId = null;
    this.editLocationAction = null; // 'move', 'relocate', or 'intransit'

    // Searchable dropdowns (custom, Select2-like).
    try {
      if (this.hasLocationSelectTarget) {
        this.ensureSearchableDropdown(this.locationSelectTarget, {
          placeholder: 'Select a Location',
        });
      }
      if (this.hasTransitLocationSelectTarget) {
        this.ensureSearchableDropdown(this.transitLocationSelectTarget, {
          placeholder: 'Select a Transit Location',
        });
      }
      if (this.hasStoreLocationSelectTarget) {
        this.ensureSearchableDropdown(this.storeLocationSelectTarget, {
          placeholder: 'Select a StoreMe Location',
        });
      }
    } catch (e) {
      console.warn('Failed to initialize searchable location selects:', e);
    }
  
    // Helper to read current form value for a given item key (input/select and _id variants)
    const readCurrent = (key) => {
      const trySelectors = [
        `input[name="item[${key}]"]`,
        `select[name="item[${key}]"]`,
        `input[name="item[${key}_id]"]`,
        `select[name="item[${key}_id]"]`,
      ];

      for (let sel of trySelectors) {
        const el = document.querySelector(sel);
        if (!el) continue;
        if (el.tagName === 'SELECT') {
          return el.selectedOptions[0]?.text || el.value || '';
        }
        return el.value || '';
      }

      return '';
    };


    this.originalDetails = {
      code: this.formTarget.dataset.itemCode || readCurrent('code'),
      usage: readCurrent('usage'),
      condition: readCurrent('condition') || readCurrent('condition_id'),
      status: readCurrent('status') || readCurrent('status_id'),
      project: readCurrent('project'),
      room: readCurrent('room'),
      purchase_date: readCurrent('purchase_date'),
      purchase_price_cents: readCurrent('purchase_price_cents') || readCurrent('purchase_price'),
      warranty: readCurrent('warranty'),
      warranty_info: readCurrent('warranty_info'),
      action: readCurrent('action'),
      action_date: readCurrent('action_date')
    };
    console.log("ORIGINAL DETAILS<><><>:", this.originalDetails);

    // Parse full item JSON and attributes JSON from form dataset (if present)
    try {
      if (this.formTarget.dataset.item) {
        this.originalItem = JSON.parse(this.formTarget.dataset.item);
      } else {
        this.originalItem = null;
      }
    } catch (e) {
      console.error('Failed to parse item JSON from form dataset or script tag:', e);
      this.originalItem = null;
    }
    console.log("ORIGINAL ITEM:::::", this.originalItem);

    try {
      if (this.formTarget.dataset.itemAttributesJson) {
        this.originalItemAttributes = JSON.parse(this.formTarget.dataset.itemAttributesJson || '[]');
      } else if (!this.originalItemAttributes) {
        this.originalItemAttributes = [];
      }
    } catch (e) {
      console.error('Failed to parse item attributes JSON from form dataset or script tag:', e);
      this.originalItemAttributes = this.originalItemAttributes || [];
    }
    console.log("ORIGINAL ITEM ATTRIBUTES:::::", this.originalItemAttributes);
    this.relocateMode = 'building'; // 'building' or 'store' for relocate
    this.relocateState = {
      locationId: null,
      childLocationId: null,
      childLocationObject: null,
      locationData: null,
      floorplanLocationId: null,
      floorplanObject: null,
      population: null,
    };
    this.intransitState = {
      locationId: null,
      childLocationId: null,
      childLocationObject: null,
      locationData: null,
    };
    this.state = {
      location: {
        mode: 'building',
        locationId: null,
        childLocationId: null,
        childLocationObject: null,
        locationData: null,
        floorplanLocationId: null,
        floorplanObject: null,
        population: null,
      },
    };

    this.validator = new WizardValidator();

    this.codeValidationTimeout = null;
    this.isCodeValid = false;

    this.bulkPlacements = [];

    this.boundBulkChanged = (event) => this.onBulkConfigChanged(event);
    document.addEventListener('items--wizard-bulk:changed', this.boundBulkChanged);

    this.boundBulkPlacementListener = (event) => this.handleBulkPlacement(event);
    document.addEventListener('floorplan:placement', this.boundBulkPlacementListener);

    // Image handling
    this.selectedImages = []; // Array of { file: File, preview: string, id: string }
    this.initializeImages();

    this.boundAttributesRendered = this.attributesRendered.bind(this);
    document.addEventListener('catalogue:attributes-rendered', this.boundAttributesRendered);

    // originalItemAttributes already parsed from form dataset earlier; ensure default
    this.originalItemAttributes = this.originalItemAttributes || [];

    this.setupFieldValidationListeners();
    this.setupCodeValidation();

    // Listen for x and y field changes to update Next button state
    this.setupPositionValidationListeners();

    // Load building locations and update UI
    this.loadLocations('building');
    this.updateLocationUI();
    
    // In edit mode, initialize existing location and x/y values
    if (isEditMode) {
      this.initializeEditMode();
    }
    
    // Initialize images (load existing photos if any)
    this.initializeImages();
    if (this.selectedImages.length > 0) {
      this.updateImageDisplay();
    }
    
    this.updateUI();
  }

  setupFieldValidationListeners() {
    // Listen for input/change events on all required fields
    const requiredFields = [
      'item[code]',
      'item[usage]',
      'item[condition_id]',
      'item[status_id]',
      // 'item[project]',
      // 'item[room]',
      // 'item[purchase_date]',
      // 'item[purchase_price_cents]',
      // 'item[warranty]',
      // 'item[warranty_info]'
    ];

    requiredFields.forEach(fieldName => {
      const field = document.querySelector(`[name="${fieldName}"]`);
      if (field) {
        field.addEventListener('input', () => {
          const fieldKey = fieldName.replace('item[', '').replace(']', '');
          this.validator.clearFieldError(fieldKey, this.state.location.mode);
        });
        field.addEventListener('change', () => {
          const fieldKey = fieldName.replace('item[', '').replace(']', '');
          this.validator.clearFieldError(fieldKey, this.state.location.mode);
        });
      }
    });

    // Listen for attribute changes
    document.addEventListener('change', (e) => {
      if (e.target.hasAttribute('data-attr-id')) {
        const attrId = e.target.getAttribute('data-attr-id');
        this.validator.clearFieldError(`attribute_${attrId}`, this.state.location.mode);
      }
    });

    // Also clear attribute errors as soon as a checkbox is toggled (multi/colour UI)
    document.addEventListener('input', (e) => {
      if (e.target && e.target.matches && e.target.matches('input[type="checkbox"][data-attr-id]')) {
        const attrId = e.target.getAttribute('data-attr-id');
        this.validator.clearFieldError(`attribute_${attrId}`, this.state.location.mode);
      }
    });
  }

  setupCodeValidation() {
    const codeField = document.querySelector('input[name="item[code]"]');
    if (codeField && !this.isEditMode) {
      codeField.addEventListener('input', (e) => {
        this.validateCodeField(e.target);
      });
      codeField.addEventListener('blur', (e) => {
        this.validateCodeField(e.target, true);
      });
    }
  }

  async validateCodeField(field, immediate = false) {
    const code = field.value.trim();
    
    // Clear previous timeout
    if (this.codeValidationTimeout) {
      clearTimeout(this.codeValidationTimeout);
    }

    // Reset validation state if empty
    if (!code) {
      this.isCodeValid = false;
      field.style.backgroundColor = '';
      field.style.borderColor = '';
      this.updateUI();
      return;
    }

    const checkCode = async () => {
      try {
        const response = await axios.get('/api/v8/items/validate_code', {
          params: { code: code }
        });
        
        if (response.data.in_use) {
          // Code is already in use
          this.isCodeValid = false;
          field.style.backgroundColor = '#f8d7da';
          field.style.borderColor = '#dc3545';
          field.style.color = '#721c24';
          
          // Show error message
          this.showCodeError(field, 'Code is already in use');
        } else {
          // Code is available
          this.isCodeValid = true;
          field.style.backgroundColor = '#d4edda';
          field.style.borderColor = '#28a745';
          field.style.color = '#155724';
          
          // Remove error message
          this.removeCodeError(field);
        }
      } catch (error) {
        console.error('Code validation error:', error);
        this.isCodeValid = false;
        field.style.backgroundColor = '#f8d7da';
        field.style.borderColor = '#dc3545';
        this.showCodeError(field, 'Error checking code availability');
      }
      
      this.updateUI();
    };

    if (immediate) {
      await checkCode();
    } else {
      // Debounce API call
      this.codeValidationTimeout = setTimeout(checkCode, 500);
    }
  }

  showCodeError(field, message) {
    // Remove existing error
    this.removeCodeError(field);
    
    // Create error element
    const errorDiv = document.createElement('div');
    errorDiv.className = 'code-validation-error';
    errorDiv.style.color = '#dc3545';
    errorDiv.style.fontSize = '0.875rem';
    errorDiv.style.marginTop = '0.25rem';
    errorDiv.textContent = message;
    
    // Insert after field
    field.parentElement.appendChild(errorDiv);
  }

  removeCodeError(field) {
    const existingError = field.parentElement.querySelector('.code-validation-error');
    if (existingError) {
      existingError.remove();
    }
  }

  setupPositionValidationListeners() {
    // Listen for changes to x and y fields to update Next button state
    // In edit mode, fields are visible number inputs, not hidden fields
    const xField = this.hasXFieldTarget ? this.xFieldTarget : document.querySelector('input[name="item[x]"]');
    const yField = this.hasYFieldTarget ? this.yFieldTarget : document.querySelector('input[name="item[y]"]');
    
    if (xField) {
      xField.addEventListener('input', () => {
        this.updateUI();
        this.clearPositionError();
      });
      xField.addEventListener('change', () => {
        this.updateUI();
        this.clearPositionError();
      });
    }
    
    if (yField) {
      yField.addEventListener('input', () => {
        this.updateUI();
        this.clearPositionError();
      });
      yField.addEventListener('change', () => {
        this.updateUI();
        this.clearPositionError();
      });
    }
  }
  
  positionChanged(event) {
    this.updateUI();
    this.clearPositionError();
  }
  
  showPositionFields() {
    const container = document.getElementById('wizard-position-fields-container');
    if (container) {
      container.style.display = 'block';
    }
  }

  showChildField(){
    const container = document.getElementById('wizard-location-children-container')
    if (container){
      container.style.display = 'block'
    }
  }
  
  hidePositionFields() {
    const container = document.getElementById('wizard-position-fields-container');
    if (container) {
      container.style.display = 'none';
    }
  }
  
  initializeEditMode() {
    if (this.hasLocationIdFieldTarget && this.locationIdFieldTarget.value) {
      const locationId = this.locationIdFieldTarget.value;
      this.state.location.locationId = locationId;
      this.selectedLocation = locationId;
      
      // Get current x/y values from the form
      const xField = document.querySelector('input[name="item[x]"]');
      const yField = document.querySelector('input[name="item[y]"]');
      if (xField && yField) {
        // Store current x/y in state for move action
        this.state.location.locationData = this.state.location.locationData || {};
        this.state.location.locationData.x = xField.value;
        this.state.location.locationData.y = yField.value;
        // Record initial position for change detection in review
        this.initialPosition = { x: xField.value || '', y: yField.value || '' };
      }
      
      // Load location details first to determine the location type
      this.loadLocationDetails(locationId).then(() => {
        // After location details are loaded, check if it's store or transit
          const locationData = this.state.location.locationData;
          if (locationData) {
          // Set location mode based on location type
          if (locationData.store) {
            this.state.location.mode = 'store';
          } else if (locationData.transit) {
            this.state.location.mode = 'transit';
          } else {
            this.state.location.mode = 'building';
          }
          
          // Check if current location is a child location
          if (locationData.parent) {
            // Current location is a child, find it in parent's children
            this.loadLocationDetails(locationData.parent.id).then((parentData) => {
              const childLocation = parentData.children?.find(
                child => child.id === Number(locationId)
              );
              if (childLocation) {
                this.state.location.childLocationId = locationId;
                this.state.location.childLocationObject = childLocation;
                // Set hidden location_id to the actual location being used (child)
                if (this.hasLocationIdFieldTarget) {
                  this.locationIdFieldTarget.value = locationId;
                }
                if (childLocation.floorplan && childLocation.floorplan.url) {
                  this.state.location.floorplanLocationId = locationId;
                  this.state.location.floorplanObject = childLocation.floorplan;
                } else {
                  this.resolveFloorplanBindingFromLocationId(locationId).then((binding) => {
                    if (binding && binding.floorplan && binding.floorplan.url) {
                      this.state.location.floorplanLocationId = binding.floorplanLocationId;
                      this.state.location.floorplanObject = binding.floorplan;
                    }
                  });
                }
              }
            });
          } else if (locationData.floorplan && locationData.floorplan.url) {
            // Parent location has floorplan
            this.state.location.floorplanLocationId = locationId;
            this.state.location.floorplanObject = locationData.floorplan;
            // Ensure hidden location_id is set to parent when parent floorplan is used
            if (this.hasLocationIdFieldTarget) {
              this.locationIdFieldTarget.value = locationId;
            }
          }
          
          // Update location UI (for read-only display)
          this.updateLocationUI();
          this.updateUI();
        }
      }).catch((error) => {
        console.error('Failed to initialize edit mode:', error);
      });
    }
  }

  clearPositionError() {
    const errorMsg = document.querySelector('.position-error-message');
    if (errorMsg) {
      errorMsg.remove();
    }
  }

  disconnect() {
    if (this.boundAttributesRendered) {
      document.removeEventListener('catalogue:attributes-rendered', this.boundAttributesRendered);
    }
    // Clear code validation timeout
    if (this.codeValidationTimeout) {
      clearTimeout(this.codeValidationTimeout);
    }
    if (this.boundBulkChanged) {
      document.removeEventListener('items--wizard-bulk:changed', this.boundBulkChanged);
    }
    if (this.boundBulkPlacementListener) {
      document.removeEventListener('floorplan:placement', this.boundBulkPlacementListener);
    }
  }

  get bulkCreateMode() {
    return this.hasFormTarget && this.formTarget.dataset.bulkCreate === 'true';
  }

  onBulkConfigChanged(event) {

    if (this.bulkCreateMode) {
      this.bulkPlacements = [];
      if (this.state.location.floorplanObject?.url) {
        this.loadFloorplanForLocation(this.state.location.floorplanLocationId);
      }
    }
    this.updateUI();
  }

  readBulkConfig() {
    const bulkRoot = document.querySelector('[data-controller*="items--wizard-bulk"]');
    if (!bulkRoot) return { quantity: 0, codes: [] };

    const qtyField = bulkRoot.querySelector('input[name="bulk[quantity]"]');
    const codesField = bulkRoot.querySelector('input[name="bulk[codes]"]');
    const quantity = parseInt(qtyField?.value, 10) || 0;
    const codes = (codesField?.value || '')
      .split(/[\s,]+/)
      .map((c) => c.trim())
      .filter(Boolean);

    return { quantity, codes };
  }

  handleBulkPlacement(event) {
    if (!this.bulkCreateMode) return;
    const { x, y } = event.detail || {};
    if (x == null || y == null) return;

    const { quantity } = this.readBulkConfig();
    if (quantity > 0 && this.bulkPlacements.length >= quantity) return;

    this.bulkPlacements.push({ x, y });
    this.updateBulkPlacementUI();
    this.updateUI();
  }

  bulkClearPlacements(event) {
    if (event?.preventDefault) event.preventDefault();
    this.bulkPlacements = [];
    if (this.floorplanInstance?.clearPlacements) {
      this.floorplanInstance.clearPlacements();
    }
    this.updateBulkPlacementUI();
    this.updateUI();
  }

  updateBulkPlacementUI() {

    if (!this.bulkCreateMode) return;

    const { quantity } = this.readBulkConfig();
    const placed = this.bulkPlacements.filter((p) => p && p.x != null && p.y != null).length;

    if (this.hasBulkPlacementTotalTarget) {
      this.bulkPlacementTotalTarget.textContent = String(quantity);
    }
    if (this.hasBulkPlacementProgressTarget) {
      this.bulkPlacementProgressTarget.textContent = `${placed} / ${quantity} placed`;
    }
    if (this.hasSinglePlacementNoticeTarget) {
      this.singlePlacementNoticeTarget.style.display = 'none';
    }
    if (this.hasBulkPlacementNoticeTarget) {
      this.bulkPlacementNoticeTarget.style.display = '';
    }
  }

  applyBulkPlacementsToHiddenFields() {
    if (!this.formTarget) return;

    let container = document.getElementById('item-bulk-positions-container');
    if (container) {
      container.innerHTML = '';
    } else {
      container = document.createElement('div');
      container.id = 'item-bulk-positions-container';
      container.style.display = 'none';
      this.formTarget.appendChild(container);
    } 

    this.bulkPlacements.forEach((pos, idx) => {
      if (!pos) return;
      const append = (name, value) => {
        if (value == null || value === '') return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = String(value);
        container.appendChild(input);
      };
      append(`bulk[positions][${idx}][x]`, pos.x);
      append(`bulk[positions][${idx}][y]`, pos.y);
    });
  }

  // locationSelected(event) {
  //   const locationId = event.target.value;
  //   if (this.hasLocationIdFieldTarget) {
  //     this.locationIdFieldTarget.value = locationId;
  //   }
  //   console.log("LOCATION ID:", locationId);
  //   this.selectedLocation = locationId;
  //   this.updateUI();
  // }

  locationModeChanged(event) {
    if (event.preventDefault) {
      event.preventDefault();
    }
    
    const mode = event.currentTarget.dataset.mode || event.currentTarget.closest('.location-mode-btn')?.dataset.mode || 'building';
    this.state.location.mode = mode;
    
    // Update tab states (similar to item show page tabs)
    document.querySelectorAll('.location-mode-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    const clickedTab = event.currentTarget.closest('.location-mode-btn') || event.currentTarget;
    clickedTab.classList.add('active');
    
    // Load locations for the selected mode
    this.loadLocations(mode);
    this.updateLocationUI();
    this.updateUI();
  }

  locationChanged(event) {
    const value = event.target.value;
    const mode = this.state.location.mode;
    
    if (!value) {
      // Clear children dropdown if location is deselected
      this.clearChildrenDropdown();
      this.state.location.locationId = null;
      this.state.location.childLocationId = null;
      this.state.location.childLocationObject = null;
      this.state.location.locationData = null;
      this.state.location.floorplanLocationId = null;
      this.selectedLocation = null;
      if (this.hasLocationIdFieldTarget) {
        this.locationIdFieldTarget.value = '';
      }
 
      this.updateUI();
      return;
    }
    
    // Update state
    this.state.location.locationId = value;
    this.selectedLocation = value;
    console.log("LOCATION ID form locationChanged:", value);
    // Immediately set hidden location_id so form always has a value
    if (this.hasLocationIdFieldTarget) {
      this.locationIdFieldTarget.value = value;
    }
    
    // Clear any field errors for location
    this.validator.clearFieldError('location_id', this.state.location.mode);

    if (!this.isEditMode) {
      // Fetch location details to get children
      this.loadLocationDetails(value);
    }
  }

  childLocationChanged(event) {
    // Support nested child selects. Select elements created by populateChildrenDropdown
    // carry a data-level attribute (1-based). We remove deeper selects when a parent changes.
    const select = event.target;
    const level = Number(select.dataset.level) || 1;
    const value = select.value || null;
    const mode = this.state.location.mode;

    // Helper to remove deeper selects
    const container = document.getElementById('wizard-location-children-container');
    const removeDeeper = (fromLevel) => {
      if (!container) return;
      const selects = Array.from(container.querySelectorAll('select[data-level]'));
      selects.forEach((s) => {
        const l = Number(s.dataset.level) || 0;
        if (l >= fromLevel) {
          this.destroySearchableDropdown(s);
          s.remove();
        }
      });
    };

    removeDeeper(level + 1);

    // If no value selected, set location_id back to appropriate parent (level 0 = parent location)
    if (!value) {
      this.state.location.childLocationId = null;
      this.state.location.childLocationObject = null;
      this.state.location.floorplanLocationId = null;
      this.state.location.floorplanObject = null;
      this.state.location.population = null;
      if (!this.isEditMode) {
        this.hidePositionFields();
        this.floorplanInstance = null;
      }
      if (this.hasLocationIdFieldTarget) {
        if (level === 1) {
          this.locationIdFieldTarget.value = this.state.location.locationId || '';
        } else if (container) {
          const prev = container.querySelector(`select[data-level="${level - 1}"]`);
          this.locationIdFieldTarget.value = prev ? (prev.value || this.state.location.locationId || '') : (this.state.location.locationId || '');
        }
      }
      // Clear hidden child_location_id if present
      if (this.hasChildLocationSelectTarget) {
        try { this.childLocationSelectTarget.value = ''; } catch (e) { }
      }
      if (!this.isEditMode && this.state.location.locationId) {
        this.loadLocationDetails(this.state.location.locationId);
      }
      this.updateUI();
      return;
    }

    // Always update location_id to current selection (will be updated again if deeper child is selected)
    if (this.hasLocationIdFieldTarget) {
      this.locationIdFieldTarget.value = value;
    }

    // Update hidden child_location_id (if present) to reflect current selection
    if (this.hasChildLocationSelectTarget) {
      try { this.childLocationSelectTarget.value = value; } catch (e) { }
    }

    // Update state
    this.state.location.childLocationId = value;
    this.state.location.childLocationObject = null;

    // Fetch selected child details to check for further children and floorplan/population
    axios.get(`/locations/${encodeURIComponent(value)}.json`)
      .then((response) => {
        const data = response.data.location || {};
        this.state.location.childLocationObject = data;
        // Check if this child has more children
        // Only load floorplan for building mode
        const needsFloorplan = (mode === 'building') && data.floorplan && data.floorplan.url;

        const applyResolvedFloorplan = (binding) => {
          if (binding && binding.floorplan && binding.floorplan.url) {
            this.state.location.floorplanLocationId = binding.floorplanLocationId;
            this.state.location.floorplanObject = binding.floorplan;
            this.state.location.population = binding.population || null;
            if (!this.isEditMode) {
              this.hidePositionFields();
              this.loadFloorplanForLocation(binding.floorplanLocationId);
            }
            return this.loadChildLocationPopulation(binding.floorplanLocationId).then(() => {
              if (
                !this.isEditMode &&
                Number(this.state.location.floorplanLocationId) === Number(binding.floorplanLocationId)
              ) {
                this.loadFloorplanForLocation(binding.floorplanLocationId);
              }
            });
          }
          this.state.location.floorplanLocationId = null;
          this.state.location.floorplanObject = null;
          this.state.location.population = null;
          if (!this.isEditMode) {
            this.hidePositionFields();
          }
          return Promise.resolve();
        };

        let floorplanWork = Promise.resolve();

        if (data.children && data.children.length > 0) {
          this.populateChildrenDropdown(data.children, level + 1, true);
          if (needsFloorplan) {
            floorplanWork = applyResolvedFloorplan({
              floorplanLocationId: data.id,
              floorplan: data.floorplan,
              population: data.population || null,
            });
          } else if (mode === 'building') { // Only load floorplan for building mode
            floorplanWork = this.resolveFloorplanBindingFromLocationId(data.id).then(applyResolvedFloorplan);
          }
        } else {
          // This is the deepest child - ensure location_id is set and use its floorplan/population
          if (this.hasLocationIdFieldTarget) {
            this.locationIdFieldTarget.value = value;
          }

          if (needsFloorplan) {
            floorplanWork = applyResolvedFloorplan({
              floorplanLocationId: data.id,
              floorplan: data.floorplan,
              population: data.population || null,
            });
          } else if (mode === 'building') { // Only load floorplan for building mode
            floorplanWork = this.resolveFloorplanBindingFromLocationId(data.id).then(applyResolvedFloorplan);
          } else {
            this.state.location.floorplanLocationId = null;
            this.state.location.floorplanObject = null;
            this.state.location.population = null;
            if (!this.isEditMode) {
              this.hidePositionFields();
            }
          }
        }

        floorplanWork.finally(() => {
          this.validator.clearFieldError('child_location_id', mode);
          this.updateUI();
        });
      })
      .catch((error) => {
        console.error('Failed to load child location details:', error);
      });
    
    this.updateUI();
  }

  loadChildLocationPopulation(childLocationId) {
    return axios
      .get(`/locations/${encodeURIComponent(childLocationId)}.json`)
      .then((response) => {
        const childLocationData = response.data.location || {};
        this.state.location.population = childLocationData.population || null;
      })
      .catch((error) => {
        console.error('Failed to load child location population:', error);
        this.state.location.population = null;
      });
  }

  /**
   * Walks up the location parent chain (via JSON `parent.id`) until a floorplan is found.
   * Used when a selected sub-location has no plan but an ancestor does.
   */
  resolveFloorplanBindingFromLocationId(locationId) {
    if (!locationId) return Promise.resolve(null);
    return axios
      .get(`/locations/${encodeURIComponent(locationId)}.json`)
      .then((response) => {
        const loc = response.data.location || {};
        if (loc.floorplan && loc.floorplan.url) {
          return {
            floorplanLocationId: loc.id,
            floorplan: loc.floorplan,
            population: loc.population || null,
          };
        }
        if (loc.parent && loc.parent.id) {
          return this.resolveFloorplanBindingFromLocationId(loc.parent.id);
        }
        return null;
      });
  }

  loadLocationDetails(locationId) {
    return axios
      .get(`/locations/${encodeURIComponent(locationId)}.json`)
      .then((response) => {
        const locationData = response.data.location || {};
        // In edit mode, set location mode based on location type
        if (this.isEditMode) {
          if (locationData.store) {
            this.state.location.mode = 'store';
          } else if (locationData.transit) {
            this.state.location.mode = 'transit';
          } else {
            this.state.location.mode = 'building';
          }
        }
        
        const mode = this.state.location.mode;
        
        console.log("LOCATION DATA;;;;;:", locationData); 
        // Store location data
        this.state.location.locationData = locationData;
        this.state.location.population = locationData.population;

        // Floorplan: use the selected location's own floorplan when present (parent or child),
        // not only the deepest leaf.
        this.state.location.floorplanLocationId = null;
        this.state.location.floorplanObject = null;
        // Only load floorplan for building mode
        if (!this.isEditMode && (mode === 'building')) {
          if (locationData.floorplan && locationData.floorplan.url) {
            this.state.location.floorplanLocationId = locationData.id;
            this.state.location.floorplanObject = locationData.floorplan;
            this.hidePositionFields();
            this.loadFloorplanForLocation(locationData.id);
            this.loadChildLocationPopulation(locationData.id).then(() => {
              if (
                Number(this.state.location.floorplanLocationId) === Number(locationData.id)
              ) {
                this.loadFloorplanForLocation(locationData.id);
              }
            });
          }
        }
        
        // Set location_id to parent initially (will be updated if child is selected)
        if (this.hasLocationIdFieldTarget) {
          this.locationIdFieldTarget.value = locationId;
        }
        console.log("this.locationIdFieldTarget.value DATA;;;;;:", this.locationIdFieldTarget.value); 
        
        // In edit mode, hide/show x/y fields based on location type
        if (this.isEditMode) {
          if (mode === 'building') {
            const xField = document.querySelector('input[name="item[x]"]');
            const yField = document.querySelector('input[name="item[y]"]');
            if (xField && yField && (xField.value || yField.value)) {
              this.showPositionFields();
            }
          } else {
            this.hidePositionFields();
          }
        }

        if (this.isEditMode) {
          // Set parent in location_id dropdown
          const parentSelect = document.querySelector('select[name="item[location_id]"]');
          if (parentSelect && locationData.parent) {
            parentSelect.value = locationData.parent.id;
            this.showChildField(),
            parentSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        
          // Set child in child_location_id dropdown
          const childSelect = document.querySelector('select[name="item[child_location_id]"]');
          if (childSelect && locationData.id) {
            // childSelect.value = locationData.id;
            this.populateChildrenDropdown(locationData)
            childSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        if (!this.isEditMode ) {
          // Check for children and populate children dropdown
          if (locationData.children && locationData.children.length > 0) {
            // Building + store: list all sub-locations; floorplan may live on the parent or any ancestor.
            this.populateChildrenDropdown(locationData.children);
          } else {
            // No children at all - clear dropdown
            this.clearChildrenDropdown();
          }
        }
        this.updateUI();
        return locationData;
      })
      .catch((error) => {
        console.error('Failed to load location details:', error);
        this.clearChildrenDropdown();
        throw error;
      });
  }

  populateChildrenDropdown(children, level = 1, append = false) {
    // children: array of child objects OR a single object
    const container = document.getElementById('wizard-location-children-container');
    if (!container) return;

    // Normalize children to array
    let items = [];
    if (!children) items = [];
    else if (Array.isArray(children)) items = children;
    else if (typeof children === 'object') items = [children];

    // If not appending at level 1, clear existing selects
    if (!append && level === 1) {
      container.innerHTML = '';
      container.style.display = items.length > 0 ? 'block' : 'none';
    } else {
      container.style.display = 'block';
    }

    // If level 1 and there's an existing Stimulus-targeted select, reuse it
    // but only reuse if the target is actually a SELECT element (not the hidden child_location_id input)
    let select = null;
    if (level === 1 && this.hasChildLocationSelectTarget && this.childLocationSelectTarget.tagName === 'SELECT') {
      select = this.childLocationSelectTarget;
      // ensure it's inside the container
      if (select.parentElement !== container) {
        container.appendChild(select);
      }
    }

    // Create a new select for deeper levels (or if no target available)
    if (!select) {
      select = document.createElement('select');
      select.className = 'form-control select';
      container.appendChild(select);
    }

    // Set dataset level to help handlers
    select.dataset.level = String(level);

    // Clear existing options
    while (select.firstChild) select.removeChild(select.firstChild);

    // Placeholder option
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select a Sub Location';
    select.appendChild(placeholder);

    // Add items
    items.forEach((child) => {
      const option = document.createElement('option');
      option.value = child.id;
      option.textContent = child.absolute_path_name || child.name || '';
      select.appendChild(option);
    });

    // Make each appended child dropdown searchable (single-select).
    this.ensureSearchableDropdown(select, { placeholder: 'Select a Sub Location' });

    // Attach change listener only once. If the select already has a Stimulus data-action, skip adding.
    const hasStimulusAction = select.getAttribute && select.getAttribute('data-action');
    if (!hasStimulusAction && !select._nestedChangeBound) {
      select.addEventListener('change', (e) => this.childLocationChanged(e));
      select._nestedChangeBound = true;
    }
  }

  clearChildrenDropdown() {
    const container = document.getElementById('wizard-location-children-container');
    if (container) {
      // remove dynamically created selects and reset any existing target select
      const selects = Array.from(container.querySelectorAll('select[data-level]'));
      selects.forEach((s) => {
        this.destroySearchableDropdown(s);
        s.remove();
      });
      container.style.display = 'none';
    }
    if (this.hasChildLocationSelectTarget) {
      try { this.childLocationSelectTarget.value = ''; } catch (e) {}
      // ensure the static select is removed from DOM to avoid duplicates next time
      if (this.childLocationSelectTarget.parentElement === container) {
        // left intentionally
      }
    }
    this.state.location.childLocationId = null;
    this.state.location.childLocationObject = null;
  }

  updateLocationUI() {
    const mode = this.state.location.mode;
    
    // Show/hide dropdowns based on mode
    document.querySelectorAll('.location-mode-dropdown').forEach(dropdown => {
      if (dropdown.dataset.mode === mode) {
        dropdown.style.display = 'block';
      } else {
        dropdown.style.display = 'none';
      }
    });
  }

  loadLocations(mode) {
    const type =
      mode === 'transit' ? 'transit' : mode === 'store' ? 'store' : 'building';

    axios
      .get(`/locations.json?type=${encodeURIComponent(type)}`)
      .then((response) => {
        const locations = response.data || [];
        this.populateLocationsForMode(mode, locations);
      })
      .catch((error) => {
        console.error('Failed to load locations:', error);
        this.populateLocationsForMode(mode, []);
      });
  }

  populateLocationsForMode(mode, locations) {
    let select = null;

    if (mode === 'transit') {
      if (!this.hasTransitLocationSelectTarget) return;
      select = this.transitLocationSelectTarget;
    } else if (mode === 'store') {
      if (!this.hasStoreLocationSelectTarget) return;
      select = this.storeLocationSelectTarget;
    } else {
      if (!this.hasLocationSelectTarget) return;
      select = this.locationSelectTarget;
    }
    // Clear existing options
    while (select.firstChild) {
      select.removeChild(select.firstChild);
    }

    const placeholder = document.createElement('option');
    placeholder.value = '';
    if (mode === 'transit') {
      placeholder.textContent = 'Select a Transit Location';
    } else if (mode === 'store') {
      placeholder.textContent = 'Select a StoreMe Location';
    } else {
      placeholder.textContent = 'Select a Location';
    }
    select.appendChild(placeholder);
    
    locations.forEach((loc) => {
      const opt = document.createElement('option');
      opt.value = loc.id;
      opt.textContent = loc.absolute_path_name || loc.name;
      select.appendChild(opt);
    });

    // Re-sync searchable dropdown so list matches new options.
    this.ensureSearchableDropdown(select, { placeholder: placeholder.textContent });
  }



  catalogueItemSelected(event) {
    const catalogueItemId = event.target.value;
    if (this.lastCatalogueItemId === catalogueItemId) {
      return;
    }
    this.lastCatalogueItemId = catalogueItemId;
    if (this.hasCatalogueItemIdFieldTarget) {
      this.catalogueItemIdFieldTarget.value = catalogueItemId;
    }
    console.log("CATALOGUE ITEM ID:", catalogueItemId);
    
    // Clear field error for catalogue item
    this.validator.clearFieldError('catalogue_item_id', this.state.location.mode);
    
    this.updateUI();
  }

  categoryItemSelected(event) {
    const hiddenField = document.getElementById("category-color-field");
    if (!hiddenField) return;
    
    const selectElement = event.target;
    const selectedValue = selectElement.value;
    
    if (!selectedValue) {
      hiddenField.value = '';
      return;
    }
    
    // Get colour mapping from data attribute on select element
    const colourMapJson = selectElement.dataset.categoryColours || selectElement.getAttribute('data-category-colours');
    
    if (colourMapJson) {
      try {
        const colourMap = JSON.parse(colourMapJson);
        const selectedCategory = colourMap.find(c => String(c.id) === String(selectedValue));
        const color = selectedCategory ? (selectedCategory.colour || '') : '';
        hiddenField.value = color;
        console.log("Selected Category ID:", selectedValue);
        console.log("Selected Colour:", color);
      } catch (e) {
        console.error('Failed to parse category colour map:', e);
      }
    } else {
      // Fallback: try to get from option dataset (if attributes were preserved)
      const option = selectElement.selectedOptions[0];
      const color = option?.dataset.colour || option?.getAttribute('data-colour') || '';
      hiddenField.value = color;
      console.log("Selected Colour (fallback):", color);
    }
  }

  // Called when attributes are rendered from get_catalogue_controller
  attributesRendered(event) {
    const attributeAssociations = event.detail.attributeAssociations || [];
    
    // Separate fixed and variable attributes
    this.fixedAttributes = attributeAssociations.filter(attr => attr.variable === false);
    this.variableAttributes = attributeAssociations.filter(attr => attr.variable === true);
    
    console.log("FIXED ATTRIBUTES:", this.fixedAttributes);
    console.log("VARIABLE ATTRIBUTES:", this.variableAttributes);

    // In edit mode, pre-populate existing attribute values
    if (this.isEditMode) {
      this.prePopulateAttributeValues();
    }
    
  }

  prePopulateAttributeValues() {
    // Get existing item attributes from a data attribute or hidden field
    const itemAttributesField = document.querySelector('[data-item-attributes]');
    if (!itemAttributesField) return;
    
    try {
      const itemAttributes = JSON.parse(itemAttributesField.dataset.itemAttributes || '[]');
      
      itemAttributes.forEach(itemAttr => {
        // Find the matching variable attribute
        const matchingAttr = this.variableAttributes.find(
          attr => attr.id === itemAttr.catalogue_association_id
        );
        
        if (!matchingAttr) return;
        
        const type = matchingAttr.owner_attribute.field_type;
        const attrId = matchingAttr.id;
        
        // Find and populate the input element
        if (type === 'colour') {
          if (!itemAttr.value) return;

          let colorValues = itemAttr.value;
          if (typeof colorValues === 'string') {
            try {
              colorValues = JSON.parse(colorValues);
            } catch (e) {
              // fallback: single id string
              colorValues = [colorValues];
            }
          }

          // New UI: checkbox group
          const selectedIds = Array.isArray(colorValues)
            ? colorValues.map((c) => (typeof c === 'object' ? String(c.id) : String(c)))
            : [String(colorValues)];

          const checkboxes = document.querySelectorAll(`input[type="checkbox"][data-attr-id="${attrId}"][name="attr_colour_${attrId}[]"]`);
          if (checkboxes.length > 0) {
            checkboxes.forEach((cb) => {
              cb.checked = selectedIds.includes(String(cb.value));
            });
            return;
          }

          // Legacy UI (select2)
          const select = document.querySelector(`select[name="attr_colour_${attrId}[]"]`);
          if (select && Array.isArray(colorValues)) {
            colorValues.forEach(color => {
              const option = Array.from(select.options).find(
                opt => opt.value === String(color.id)
              );
              if (option) option.selected = true;
            });
            if (window.$ && window.$(select).data('select2')) {
              window.$(select).trigger('change');
            }
          }
        } else {
          const input = document.querySelector(`[data-attr-id="${attrId}"]`);
          if (!input || !itemAttr.value) return;

          if (type === 'multi_select' || type === 'multiple_select') {
            let values = itemAttr.value;
            if (typeof values === 'string') {
              try {
                values = JSON.parse(values);
              } catch (e) {
                values = [values];
              }
            }

            const selected = Array.isArray(values) ? values.map(String) : [String(values)];

            // New UI: checkbox group
            const checkboxes = document.querySelectorAll(`input[type="checkbox"][data-attr-id="${attrId}"]`);
            if (checkboxes.length > 0) {
              checkboxes.forEach((cb) => {
                cb.checked = selected.includes(String(cb.value));
              });
              return;
            }

            // Legacy UI: <select multiple>
            if (input.tagName === 'SELECT' && Array.isArray(values)) {
              Array.from(input.options).forEach(option => {
                if (selected.includes(String(option.value))) {
                  option.selected = true;
                }
              });
            }
            return;
          }

          input.value = itemAttr.value;
        }
      });
    } catch (error) {
      console.error('Error pre-populating attribute values:', error);
    }
  }

  // Collect variable attribute values from form inputs
  collectVariableAttributes() {
    const attributes = [];
    
    if (!this.variableAttributes || this.variableAttributes.length === 0) {
      return attributes;
    }
    
    this.variableAttributes.forEach(attr => {
      const type = attr.owner_attribute.field_type;
      let value = null;
      
      // Find the input element for this attribute
      let inputElement = null;
      
      if (type === 'colour') {
        // New UI: checkbox group
        const checked = document.querySelectorAll(`input[type="checkbox"][data-attr-id="${attr.id}"][name="attr_colour_${attr.id}[]"]:checked`);
        if (checked.length > 0) {
          value = Array.from(checked).map((cb) => ({
            id: cb.value,
            name: cb.parentElement?.innerText?.trim() || cb.value,
            colour_type: cb.dataset.colourType,
            value: cb.dataset.value,
          }));
        } else {
          // Legacy UI: select2 multi
          inputElement = document.querySelector(`select[name="attr_colour_${attr.id}[]"]`);
          if (inputElement) {
            const selectedOptions = Array.from(inputElement.selectedOptions);
            if (selectedOptions.length > 0) {
              value = selectedOptions.map(option => ({
                id: option.value,
                name: option.text,
                colour_type: option.dataset.colourType,
                value: option.dataset.value
              }));
            }
          }
        }
      } else {
        // For other types, find by data attribute
        const container = document.getElementById('catalogueItemAttributes');
        if (container) {
          // Find input within the attributes container
          const inputs = container.querySelectorAll(`input[data-attr-id="${attr.id}"], select[data-attr-id="${attr.id}"]`);
          if (inputs.length > 0) {
            inputElement = inputs[0];
          }
        }
        
        if (inputElement) {
          if (type === 'multi_select' || type === 'multiple_select') {
            // New UI: checkbox group
            const checked = container
              ? container.querySelectorAll(`input[type="checkbox"][data-attr-id="${attr.id}"]:checked`)
              : document.querySelectorAll(`input[type="checkbox"][data-attr-id="${attr.id}"]:checked`);

            if (checked.length > 0) {
              value = Array.from(checked).map((cb) => cb.value);
            } else if (inputElement.tagName === 'SELECT') {
              // Legacy UI: <select multiple>
              const selectedOptions = Array.from(inputElement.selectedOptions);
              value = selectedOptions.length > 0 ? selectedOptions.map(option => option.value) : null;
            } else {
              value = null;
            }
          } else {
            value = inputElement.value || null;
          }
        }
      }
      
      // Only add if value is not null/empty
      if (value !== null && value !== '' && (Array.isArray(value) ? value.length > 0 : true)) {
        attributes.push({
          catalogue_association_id: attr.id,
          catalogue_attribute_id: attr.owner_attribute.id,
          catalogue_attribute_name: attr.owner_attribute.name,
          catalogue_attribute_type: type,
          value: value,
          variable: attr.variable,
        });
      }
    });
    
    return attributes;
  }

  // Collect all attributes (fixed + variable) for form submission
  collectAllAttributes() {
    const allAttributes = [];
    
    // Add fixed attributes
    this.fixedAttributes.forEach(attr => {
      allAttributes.push({
        catalogue_association_id: attr.id,
        catalogue_attribute_id: attr.owner_attribute.id,
        catalogue_attribute_name: attr.owner_attribute.name,
        catalogue_attribute_type: attr.owner_attribute.field_type,
        value: attr._value || attr.value,
        variable: attr.variable,
      });
    });
    
    // Add variable attributes with their values
    const variableAttrs = this.collectVariableAttributes();
    allAttributes.push(...variableAttrs);
    
    return allAttributes;
  }

  // Prepare attributes data (called when clicking Next button)
  prepareAttributes() {
    this.preparedAttributes = this.collectAllAttributes();
    console.log("PREPARED ATTRIBUTES:", this.preparedAttributes);
    return this.preparedAttributes;
  }

  // Initialize images from existing photos (edit mode)
  initializeImages() {
    
    const thumbnailsContainer = document.getElementById('item-images-thumbnails');
    if (!thumbnailsContainer) return;

    // If there are existing photos, set them up
    const existingThumbnails = thumbnailsContainer.querySelectorAll('.image-thumbnail-wrapper');
    if (existingThumbnails.length > 0) {
      existingThumbnails.forEach((thumb, index) => {
        const img = thumb.querySelector('img');
        const photoId = img?.dataset?.photoId;
        if (photoId && !this.selectedImages.some(image => image.id === photoId)) {
          this.selectedImages.push({
            id: photoId,
            preview: img.src,
            isExisting: true,
            index: index
          });
        }
      });
    }
  }

  // Handle multiple image selection
  imagesSelected(event) {
    
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        console.warn('Skipping non-image file:', file.name);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = {
          file: file,
          preview: e.target.result,
          id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          isExisting: false
        };

        this.selectedImages.push(imageData);
        this.updateImageDisplay();
      };

      reader.readAsDataURL(file);
    });

    // Reset the input so the same files can be selected again
    event.target.value = '';
  }

  // Remove an image
  removeImage(event) {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    
    const target = event?.currentTarget || event?.target || event;
    const photoId = target.dataset?.photoId || target.getAttribute?.('data-photo-id');
    
    if (!photoId) return;

    // If this is an existing image (from server), record it for removal
    const existingImg = this.selectedImages.find(img => String(img.id) === String(photoId) && img.isExisting);
    if (existingImg) {
      // Ensure we have a container for removed photo ids
      let removedContainer = document.getElementById('item-removed-photos-container');
      if (!removedContainer) {
        removedContainer = document.createElement('div');
        removedContainer.id = 'item-removed-photos-container';
        removedContainer.style.display = 'none';
        this.formTarget.appendChild(removedContainer);
      }
      // Add hidden input to mark this photo id for deletion on server
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'item[removed_photo_ids][]';
      input.value = photoId;
      removedContainer.appendChild(input);
    }

    // Find and remove the image from selectedImages if present
    const index = this.selectedImages.findIndex(img => img.id === photoId || img.id === photoId.toString());
    if (index !== -1) {
      this.selectedImages.splice(index, 1);
      this.updateImageDisplay();
    } else {
      // Try to remove the thumbnail DOM element if it's not in selectedImages
      const thumbnailsContainer = document.getElementById('item-images-thumbnails');
      if (thumbnailsContainer) {
        const thumbnail = target.closest?.('.image-thumbnail-wrapper');
        if (thumbnail) {
          thumbnail.remove();
          this.updateImageDisplay();
        }
      }
    }
  }

  // Update the image display (main preview and thumbnails)
  updateImageDisplay() {
    
    const mainHolder = document.getElementById('item-image-holder');
    const thumbnailsContainer = document.getElementById('item-images-thumbnails');

    if (!mainHolder || !thumbnailsContainer) return;

    // Clear thumbnails
    thumbnailsContainer.innerHTML = '';

    if (this.selectedImages.length === 0) {
      // Show placeholder
      mainHolder.innerHTML = `
        <div class="placeholder-text" style="color: #999; text-align: center; padding: 2rem;">
          No image selected<br>
          <span style="font-size: 0.875rem;">Select images below</span>
        </div>
      `;
      thumbnailsContainer.innerHTML = `
        <div class="no-images-placeholder" style="width: 100%; text-align: center; color: #999; padding: 1rem; font-size: 0.875rem;">
          No images selected
        </div>
      `;
      return;
    }

    // Update main preview with first image
    const firstImage = this.selectedImages[0];
    const mainImg = document.createElement('img');
    mainImg.src = firstImage.preview;
    mainImg.className = '-size_x100 -size-equal -fit-cover -variation-rounded';
    mainImg.style.cssText = 'width: 100%; height: 100%; object-fit: cover; border-radius: 8px;';
    mainImg.dataset.target = 'items--wizard.mainImagePreview';
    mainHolder.innerHTML = '';
    mainHolder.appendChild(mainImg);
    
    // Update thumbnails
    this.selectedImages.forEach((imageData, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'image-thumbnail-wrapper';
      wrapper.style.cssText = `position: relative; width: 80px; height: 80px; border-radius: 4px; overflow: hidden; border: 2px solid ${index === 0 ? '#3498db' : '#ddd'}; cursor: pointer;`;
      
      const img = document.createElement('img');
      img.src = imageData.preview;
      img.className = '-fit-cover';
      img.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
      img.dataset.photoId = imageData.id;
      img.dataset.photoIndex = index;
      
      // Make thumbnail clickable to change main preview
      wrapper.addEventListener('click', () => {
        this.setMainPreview(index);
      });

      const removeBtn = document.createElement('button');
      removeBtn.innerHTML = '×';
      removeBtn.style.cssText = 'position: absolute; top: 2px; right: 2px; background: rgba(255, 0, 0, 0.8); color: white; border: none; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 12px; line-height: 1; padding: 0;';
      removeBtn.dataset.action = 'click->items--wizard#removeImage';
      removeBtn.dataset.photoId = imageData.id;
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeImage({ currentTarget: removeBtn, stopPropagation: () => {} });
      });

      wrapper.appendChild(img);
      wrapper.appendChild(removeBtn);
      thumbnailsContainer.appendChild(wrapper);
    });
  }

  // Set main preview to a specific image
  setMainPreview(index) {
    if (index < 0 || index >= this.selectedImages.length) return;

    // Reorder images array to put selected image first
    const selectedImage = this.selectedImages[index];
    this.selectedImages.splice(index, 1);
    this.selectedImages.unshift(selectedImage);

    this.updateImageDisplay();
  }

  // Convert images to base64 for form submission
  async prepareImagesForSubmission() {
    const imagePromises = this.selectedImages.map(async (imageData) => {
      if (imageData.isExisting) {
        // For existing images in edit mode, skip them as they're already saved
        // Only send new images
        return null;
      }

      // Convert file to base64 with full data URI
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          // Keep the full data URI format (data:image/...;base64,...)
          // The backend's convert_base64_to_image expects this format
          const fullDataURI = e.target.result;
          resolve({
            uuid: imageData.id,
            image: fullDataURI,
            created_at: new Date().toISOString()
          });
        };
        reader.readAsDataURL(imageData.file);
      });
    });

    const images = await Promise.all(imagePromises);
    return images.filter(img => img !== null);
  }

  // Handle form submission - add attributes before submitting
  async handleSubmit(event) {
    // Prevent native form submission until we've finished async work
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    // Prevent double-submit
    if (this.submitting) return;
    this.submitting = true;

    // Ensure preloaded fields are disabled before submission
    this.disablePreloadedFields();
    
    // Ensure x and y values are preserved (especially for create mode with floorplan)
    if (!this.isEditMode) {
      // In create mode, ensure hidden x/y fields have values
      const xField = this.hasXFieldTarget ? this.xFieldTarget : document.querySelector('input[name="item[x]"]');
      const yField = this.hasYFieldTarget ? this.yFieldTarget : document.querySelector('input[name="item[y]"]');
      
      if (xField && !xField.value) {
        // Try to get value from any x field (in case floorplan set it differently)
        const anyXField = document.querySelector('input[name="item[x]"]');
        if (anyXField && anyXField.value) {
          xField.value = anyXField.value;
        }
      }
      
      if (yField && !yField.value) {
        // Try to get value from any y field (in case floorplan set it differently)
        const anyYField = document.querySelector('input[name="item[y]"]');
        if (anyYField && anyYField.value) {
          yField.value = anyYField.value;
        }
      }
      
      console.log("X value before submit:", xField?.value);
      console.log("Y value before submit:", yField?.value);
    }
    // Prepare images for submission
    const images = await this.prepareImagesForSubmission();
    if (images.length > 0) {
      // Create or get container for photos
      let photosContainer = document.getElementById('item-photos-container');
      if (!photosContainer) {
        photosContainer = document.createElement('div');
        photosContainer.id = 'item-photos-container';
        photosContainer.style.display = 'none';
        this.formTarget.appendChild(photosContainer);
      } else {
        photosContainer.innerHTML = '';
      }

      // Add hidden fields for each photo
      images.forEach((photoData, index) => {
        const fields = [
          { name: `item[photos_attributes][${index}][uuid]`, value: photoData.uuid },
          { name: `item[photos_attributes][${index}][image]`, value: photoData.image },
          { name: `item[photos_attributes][${index}][created_at]`, value: photoData.created_at }
        ];

        fields.forEach(field => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = field.name;
          input.value = field.value;
          photosContainer.appendChild(input);
        });
      });

      console.log("SUBMITTING WITH PHOTOS:", images);
    }
    // Ensure the native file input doesn't submit files (we're sending hidden fields instead)
    try {
      const fileInput = this.hasImageFieldTarget ? this.imageFieldTarget : document.getElementById('item-image-field');
      if (fileInput) {
        // Remove name so browser won't include it in form data
        fileInput.removeAttribute('name');
        // Clear files
        try { fileInput.value = ''; } catch (e) { /* ignore */ }
        // Disable the input
        fileInput.disabled = true;
      }
    } catch (e) {
      console.warn('Could not clear native file input before submit:', e);
    }
    
    // Use prepared attributes if available, otherwise collect fresh
    const allAttributes = this.preparedAttributes.length > 0 
      ? this.preparedAttributes 
      : this.collectAllAttributes();
    
    if (allAttributes.length > 0) {
      // Create hidden fields for each attribute
      const container = document.getElementById('item-attributes-container');
      if (container) {
        // Clear existing attribute fields
        container.innerHTML = '';
        
        // Add hidden fields for each attribute
        allAttributes.forEach((attr, index) => {
          // Create hidden input for each attribute field
          const fields = [
            { name: `item[item_attributes][${index}][catalogue_association_id]`, value: attr.catalogue_association_id },
            { name: `item[item_attributes][${index}][catalogue_attribute_id]`, value: attr.catalogue_attribute_id },
            { name: `item[item_attributes][${index}][catalogue_attribute_name]`, value: attr.catalogue_attribute_name },
            { name: `item[item_attributes][${index}][catalogue_attribute_type]`, value: attr.catalogue_attribute_type },
            { name: `item[item_attributes][${index}][value]`, value: typeof attr.value === 'object' ? JSON.stringify(attr.value) : attr.value },
            { name: `item[item_attributes][${index}][variable]`, value: attr.variable }
          ];
          
          fields.forEach(field => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = field.name;
            input.value = field.value;
            container.appendChild(input);
          });
        });
      }
    }
    
    console.log("SUBMITTING WITH ATTRIBUTES:", allAttributes);
    if (!this.isEditMode) {
      try {
        const locVal = this.state.location.childLocationId || this.state.location.locationId || '';
            if (this.hasChildLocationSelectTarget) {
              try { this.childLocationSelectTarget.value = value; } catch (e) { }
            }
            
            if (mode === 'building' && data.floorplan && data.floorplan.url) {
          this.childLocationSelectTarget.value = locVal;
        } 
        console.log('Submitting location_id =', locVal);
      } catch (e) {
        console.warn('Could not set location_id before submit:', e);
      }
    }

    if (this.bulkCreateMode) {
      this.applyBulkPlacementsToHiddenFields();
    }

    // Finally, submit the form programmatically so the server receives the
    // hidden fields we just injected (including photos_attributes)
    try {
      if (this.formTarget && typeof this.formTarget.submit === 'function') {
        this.formTarget.submit();
      } else {
        // Fallback: find nearest form element and submit
        const formEl = document.querySelector('form');
        if (formEl) formEl.submit();
      }
    } catch (e) {
      console.error('Failed to submit form programmatically:', e);
    } finally {
      this.submitting = false;
    }
  }

  goToStep(event) {
    const stepEl = event.currentTarget;
    const targetStep = parseInt(stepEl.dataset.step, 10);
    if (isNaN(targetStep) || targetStep < 1 || targetStep > this.maxStep) return;
    if (targetStep === this.currentStep) return;

    // Only allow going to previously completed steps (not forward skipping)
    if (targetStep > this.currentStep) return;

    this.currentStep = targetStep;
    
    // When going back to step 1, clean up floorplan
    if (this.currentStep === 1 && this.floorplanInstance) {
      if (this.isEditMode) {
        const moveContainer = document.getElementById('wizard-edit-floorplan-container');
        const relocateContainer = document.getElementById('wizard-relocate-floorplan-inner');
        if (moveContainer) {
          moveContainer.innerHTML = '<div class="placeholder" style="display: flex; align-items: center; justify-content: center; height: 100%;"><div class="placeholder-message"><p style="color: #8e9ab5; font-size: 13px;">Loading floorplan...</p></div></div>';
        }
        if (relocateContainer) {
          relocateContainer.innerHTML = '<div class="placeholder" style="display: flex; align-items: center; justify-content: center; height: 100%;"><div class="placeholder-message"><p style="color: #8e9ab5; font-size: 13px;">Loading floorplan...</p></div></div>';
        }
      }
    }
    
    // If going to review step, populate it
    if (this.currentStep === this.maxStep) {
      this.populateReview();
    }
    
    this.updateUI();
  }

  prev() {
    if (this.currentStep <= 1) return;
    this.currentStep -= 1;
    
    // Clean up floorplan when going back from step 2
    if (this.currentStep === 1 && this.floorplanInstance) {
      if (this.isEditMode) {
        // Clean up edit mode floorplan containers
        const moveContainer = document.getElementById('wizard-edit-floorplan-container');
        const relocateContainer = document.getElementById('wizard-relocate-floorplan-inner');
        if (moveContainer) {
          moveContainer.innerHTML = '<div class="placeholder" style="display: flex; align-items: center; justify-content: center; height: 100%;"><div class="placeholder-message"><p class="-size-medium -color-gray_dark">Loading floorplan...</p></div></div>';
        }
        if (relocateContainer) {
          relocateContainer.innerHTML = '<div class="placeholder" style="display: flex; align-items: center; justify-content: center; height: 100%;"><div class="placeholder-message"><p class="-size-medium -color-gray_dark">Loading floorplan...</p></div></div>';
        }
        
        // Reset edit mode state
        this.editLocationAction = null;
        document.getElementById('wizard-edit-move-container').style.display = 'none';
        document.getElementById('wizard-edit-relocate-container').style.display = 'none';
        document.getElementById('wizard-edit-intransit-container').style.display = 'none';
        
        // Reset location action select
        if (this.hasLocationActionSelectTarget) {
          this.locationActionSelectTarget.value = '';
        }
      } else {
        // Clean up create mode floorplan
        const container = document.getElementById('wizard-floorplan-container');
        if (container) {
          container.innerHTML = '<div class="placeholder" style="display: flex; align-items: center; justify-content: center; height: 100%;"><div class="placeholder-message"><p class="-size-medium -color-gray_dark">Loading floorplan...</p></div></div>';
        }
      }
      this.floorplanInstance = null;
    }
    
    this.updateUI();
  }

  next() {
    if (this.currentStep >= this.maxStep) return;
    
    // Validate fields only when clicking Next button (Step 1)
    if (this.currentStep === 1) {
      const errors = this.validator.validateFields(
        this.state,
        this.fixedAttributes,
        this.variableAttributes,
        () => this.collectAllAttributes(),
        () => this.collectVariableAttributes(),
        this.isEditMode,
        false,
        this.bulkCreateMode
      );
      // Show errors if any
      if (errors.length > 0) {
        this.validator.showFieldErrors(errors, this.state.location.mode);
        return;
      }
      
      // Clear errors if validation passes
      this.validator.clearAllErrors();
      
      // Prepare attributes data before moving to next step
      this.prepareAttributes();
      
      // Disable preloaded_categories field when leaving step 1
      this.disablePreloadedFields();
      // In edit mode, go to step 2 (location change)
      if (this.isEditMode) {
        // When entering step 2 in edit mode, default to keeping the existing location
        // by pre-selecting the "move" action. This allows the user to either:
        // - keep the current location and simply proceed, or
        // - change the action (Relocate / In Transit) explicitly.
        if (this.editLocationAction) {

          // Initialize the UI for the move action (loads floorplan / messaging)
          this.handleMoveAction();
        }
      }
    }
    
    // Validate step 2 for edit mode
    if (this.currentStep === 2 && this.isEditMode) {
      // if (!this.editLocationAction) {
      //   // Show error if no action selected
      //   alert('Please select an action (Move, Relocate, or In Transit)');
      //   return;
      // }
      if (this.editLocationAction === 'move') {
        // For move action, check if location has floorplan
        const mode = this.state.location.mode;
        if (mode === 'building') {
          // Validate x/y for building locations with floorplan
          const xField = document.querySelector('input[name="item[x]"]');
          const yField = document.querySelector('input[name="item[y]"]');
          const x = xField ? xField.value : '';
          const y = yField ? yField.value : '';
          
          if (!x || !y || x.trim() === '' || y.trim() === '') {
            this.showPositionError();
            return;
          }
        }
        // For transit/store locations, no x/y validation needed (no floorplan)
        
        // Update location_id field to current location (should already be set)
        if (this.hasLocationIdFieldTarget) {
          const locationId = this.state.location.childLocationId || this.state.location.locationId;
          this.locationIdFieldTarget.value = locationId;
        }
      } else if (this.editLocationAction === 'relocate') {
        const mode = this.relocateMode;
        let locationId = null;
        
        if (mode === 'store') {
          if (!this.relocateState.locationId) {
            // No new location selected, use existing location
            locationId = this.state.location.childLocationId || this.state.location.locationId;
          } else {
            locationId = this.relocateState.childLocationId || this.relocateState.locationId;
          }
        } else {
          if (!this.relocateState.locationId) {
            // No new location selected, use existing location
            locationId = this.state.location.childLocationId || this.state.location.locationId;
          } else {
            // For building, check if floorplan is required
            if (this.relocateState.childLocationId) {
              locationId = this.relocateState.childLocationId;
              // Validate x/y if floorplan was shown
              if (this.relocateState.floorplanObject) {
                const xField = document.querySelector('input[name="item[x]"]');
                const yField = document.querySelector('input[name="item[y]"]');
                const x = xField ? xField.value : '';
                const y = yField ? yField.value : '';
                
                if (!x || !y || x.trim() === '' || y.trim() === '') {
                  this.showPositionError();
                  return;
                }
              }
            } else {
              locationId = this.relocateState.locationId;
            }
          }
        }
        
        // Update location_id field
        if (this.hasLocationIdFieldTarget) {
          this.locationIdFieldTarget.value = locationId;
        }
      } else if (this.editLocationAction === 'intransit') {
        // Validate location selection for intransit
        // If no new location is selected, fall back to existing location
        let locationId = null;
        
        if (!this.intransitState.locationId) {
          // No new location selected, use existing location
          locationId = this.state.location.childLocationId || this.state.location.locationId;
        } else {
          locationId = this.intransitState.childLocationId || this.intransitState.locationId;
        }
        
        // Update location_id field
        if (this.hasLocationIdFieldTarget) {
          this.locationIdFieldTarget.value = locationId;
        }
      } else if (this.editLocationAction === null) {
        const mode = this.state.location.mode;
        const locationId = this.state.location.locationData || this.state.location.childLocationId;
      }
      
      // Clear position error if validation passes
      this.clearPositionError();
      
      // Populate review
      this.populateReview();
    }
    
    // Step 2 → 3 (create): x/y required when a floorplan is shown (building or store child)
    if (this.currentStep === 2 && !this.isEditMode) {
      const mode = this.state.location.mode;
      const needsPosition =
        (mode === 'building') && this.state.location.floorplanObject?.url; // Only load floorplan for building mode
      if (needsPosition && !this.bulkCreateMode) {
        const x = this.hasXFieldTarget ? this.xFieldTarget.value : '';
        const y = this.hasYFieldTarget ? this.yFieldTarget.value : '';

        if (!x || !y || x.trim() === '' || y.trim() === '') {
          this.showPositionError();
          return;
        }

        this.clearPositionError();
      } else if (needsPosition && this.bulkCreateMode) {
        this.clearPositionError();
      }
      this.populateReview();
    }
    
    this.currentStep += 1;
    this.updateUI();
  }

  showPositionError() {
    // Clear any existing error
    this.clearPositionError();
    
    let container = null;
    let errorMessage = '';
    
    if (this.isEditMode) {
      // In edit mode, show error near position fields
      container = document.getElementById('wizard-position-fields-container');
      errorMessage = 'Please enter both X and Y coordinates before proceeding.';
    } else {
      container = document.getElementById('wizard-floorplan-container');
      errorMessage = this.bulkCreateMode
        ? 'Click the floorplan to place items, or use Auto-fill remaining to continue.'
        : 'Please click on the floorplan to set the position (x, y coordinates) before proceeding.';
    }
    
    if (!container) return;
    
    // Create error message element
    const errorMsg = document.createElement('div');
    errorMsg.className = 'position-error-message';
    errorMsg.style.cssText = 'color: #e74c3c; font-size: 0.875rem; margin-top: 1rem; padding: 0.75rem; background-color: #fee; border: 1px solid #e74c3c; border-radius: 4px;';
    errorMsg.textContent = errorMessage;
    
    // Insert error message after the container
    const parent = container.parentElement;
    if (parent) {
      parent.insertBefore(errorMsg, container.nextSibling);
    }
  }

  // Disable preloaded fields so they don't get submitted
  disablePreloadedFields() {
    const preloadedCategoriesField = document.querySelector('input[name="preloaded_categories"]');
    const hasCatalogueIdField = document.querySelector('input[name="has_catalogue_id"]');
    
    if (preloadedCategoriesField) {
      preloadedCategoriesField.disabled = true;
      // Also remove the name attribute to ensure it's not submitted
      preloadedCategoriesField.removeAttribute('name');
    }
    
    if (hasCatalogueIdField) {
      hasCatalogueIdField.disabled = true;
      hasCatalogueIdField.removeAttribute('name');
    }
  }

  loadFloorplanForLocation(locationId) {
    const floorplanLocationId = this.state.location.floorplanLocationId;
    const floorplan = this.state.location.floorplanObject;
    
    if (!floorplanLocationId || !floorplan || !floorplan.url) {
      const container = document.getElementById('wizard-floorplan-container');
      if (container) {
        container.innerHTML = '<div class="card -color-gray -no-border -padding_2"><p class="-size-medium -color-gray_dark">No floorplan available for this location. Choose a location that has a floorplan, or pick a sub-location that provides one.</p></div>';
      }
      return;
    }
    
    // Keep hidden location_id aligned with the location whose floorplan is shown
    if (this.hasLocationIdFieldTarget) {
      this.locationIdFieldTarget.value = floorplanLocationId;
    }
    
    // Render floorplan using stored data (no API call needed)
    this.renderFloorplan(floorplan, floorplanLocationId, this.state.location.population);
  }

  renderFloorplan(floorplan, locationId, population = null) {
    const container = document.getElementById('wizard-floorplan-container');
    const Topcontainer = document.getElementById('wizard-floorplan-top');

    if (!container) return;
    Topcontainer.style.display = 'block';
    container.style.display = 'block';
    
    // Population from backend is already a JSON string, don't stringify again
    // If it's already a string, use it as-is. If it's an object/array, stringify it.
    let itemsData = '[]';
    if (population) {
      if (typeof population === 'string') {
        itemsData = population; // Already JSON string from backend
      } else {
        itemsData = JSON.stringify(population); // Convert to JSON string
      }
    }
    
    // Current item data - empty object for new item being created
    const currentItemData = '{}';

    const { quantity, codes } = this.readBulkConfig();
    const placementAttrs = this.bulkCreateMode && quantity > 0
      ? `data-placement-mode="multi" data-placement-cap="${quantity}" data-placement-labels='${JSON.stringify(codes)}'`
      : 'data-placement-mode="single"';

    container.innerHTML = `
      <div data-floorplan="" 
           data-view-mode="write" 
           data-type="location" 
           data-model="item"
           ${placementAttrs}
           data-plan="${floorplan.url}" 
           data-width="${floorplan.width || 0}" 
           data-height="${floorplan.height || 0}" 
           data-items='${itemsData}'
           data-item='${currentItemData}'
           style="height: 600px;">
        <div class="placeholder">
          <div class="placeholder-message">Initializing Floorplan..</div>
        </div>
      </div>
    `;

    // Initialize floorplan component
    setTimeout(() => {
      try {
        this.floorplanInstance = new Floorplan();
        this.floorplanInstance.initialize();
      } catch (e) {
        console.error('Failed to initialize floorplan:', e);
      }
    }, 100);
  }

  populateReview() {
    // Populate catalogue review
    const catalogueField = document.querySelector('input[name="item[catalogue_item_id]"]');
    const catalogueReview = document.getElementById('wizard-review-catalogue');
    if (catalogueReview && catalogueField && catalogueField.value) {
      // Prefer the visible name/code from the catalogue selection UI (hide UUID in review)
      const nameEl = document.querySelector('#catalogueItem .cat-name');
      const codeEl = document.querySelector('#catalogueItem .cat-code-tag');
      const name = nameEl?.textContent?.trim();
      const code = codeEl?.textContent?.trim();

      if (name) {
        const codeHtml = code ? ` <span class="-color-gray_dark" style="font-family: var(--mono); font-size: 12px;">(${code})</span>` : '';
        catalogueReview.innerHTML = `<p class="-line-reset">Catalogue Style: <strong>${name}</strong>${codeHtml}</p>`;
      } else {
        catalogueReview.innerHTML = `<p class="-line-reset">Catalogue Style selected</p>`;
      }
    }
    // Populate location review
    const locationReview = document.getElementById('wizard-review-location');
    if (locationReview) {
      let locationName = 'Not set';
      
      if (this.isEditMode) {
        // For edit mode, check which action was selected
        if (this.editLocationAction === 'move') {
          // Show current location (prefer child if set)
          const child = this.state.location.childLocationObject;
          const locationData = this.state.location.locationData;
          if (child) {
            locationName = child.absolute_path_name || child.name || 'Not set';
          } else if (locationData) {
            locationName = locationData.absolute_path_name || locationData.name || locationData.x + ', ' + locationData.y || 'Not set';
          }
        } else if (this.editLocationAction === 'relocate') {
          // Prefer relocate child, then relocate parent, then existing
          const childLocation = this.relocateState.childLocationObject;
          const locationData = this.relocateState.locationData;
          if (childLocation) {
            locationName = childLocation.absolute_path_name || childLocation.name || 'Not set';
          } else if (locationData) {
            locationName = locationData.absolute_path_name || locationData.name || 'Not set';
          } else {
            const existingLocationData = this.state.location.locationData;
            if (existingLocationData) {
              // Prefer child if the saved item was in a child location
              const existingChild = this.state.location.childLocationObject;
              if (existingChild) {
                locationName = existingChild.absolute_path_name || existingChild.name || 'Not set';
              } else {
                locationName = existingLocationData.absolute_path_name || existingLocationData.name || 'Not set';
              }
            }
          }
        } else if (this.editLocationAction === 'intransit') {
          // Prefer intransit child, then intransit parent, then existing
          const childLocation = this.intransitState.childLocationObject;
          const locationData = this.intransitState.locationData;
          if (childLocation) {
            locationName = childLocation.absolute_path_name || childLocation.name || 'Not set';
          } else if (locationData) {
            locationName = locationData.absolute_path_name || locationData.name || 'Not set';
          } else {
            const existingLocationData = this.state.location.locationData;
            if (existingLocationData) {
              const existingChild = this.state.location.childLocationObject;
              if (existingChild) {
                locationName = existingChild.absolute_path_name || existingChild.name || 'Not set';
              } else {
                locationName = existingLocationData.absolute_path_name || existingLocationData.name || 'Not set';
              }
            }
          }
        } else {
          // Fallback to current location (prefer child)
          const child = this.state.location.childLocationObject;
          const locationData = this.state.location.locationData;
          if (child) {
            locationName = child.absolute_path_name || child.name || 'Not set';
          } else if (locationData) {
            locationName = locationData.absolute_path_name || locationData.name || 'Not set';
          }
        }
      } else {
        // Create mode
        if (this.state.location.childLocationObject) {
          locationName = this.state.location.childLocationObject.absolute_path_name || this.state.location.childLocationObject.name || 'Not set';
        } else if (this.state.location.locationData) {
          locationName = this.state.location.locationData.absolute_path_name || this.state.location.locationData.name || 'Not set';
        }
      }
      
      locationReview.innerHTML = `<p class="-line-reset">Location: <strong>${locationName}</strong></p>`;
    }
  
    const positionReview = document.getElementById('wizard-review-position');
    const isIntransit = this.editLocationAction === 'intransit';
    let hasLocationData = false;
    if (this.editLocationAction === 'relocate') {
      hasLocationData = !!this.relocateState.locationData;
    } else if (this.editLocationAction === 'intransit') {
      hasLocationData = !!this.intransitState.locationData;
    } else {
      // move or default
      hasLocationData = !!this.state.location.locationData;
    }

    if (!isIntransit && positionReview && hasLocationData) {
      // Try to get x/y from Stimulus targets first, then fallback to querySelector
      let x = '';
      let y = '';

      if (this.hasXFieldTarget && this.xFieldTarget.value) {
        x = this.xFieldTarget.value;
      } else {
        const xField = document.querySelector('input[name="item[x]"]');
        x = xField ? xField.value : '';
      }

      if (this.hasYFieldTarget && this.yFieldTarget.value) {
        y = this.yFieldTarget.value;
      } else {
        const yField = document.querySelector('input[name="item[y]"]');
        y = yField ? yField.value : '';
      }

      // Only show position if it actually changed from the initial values (edit mode)
      if (this.isEditMode) {
        const initX = (this.initialPosition && this.initialPosition.x) ? String(this.initialPosition.x) : '';
        const initY = (this.initialPosition && this.initialPosition.y) ? String(this.initialPosition.y) : '';
        if ((String(x) !== initX) || (String(y) !== initY)) {
          positionReview.innerHTML = `<p class="-line-reset">Position: <strong>X: ${x}, Y: ${y}</strong></p>`;
        } else {
          positionReview.innerHTML = '<p class="-line-reset -color-gray_dark">No position changes</p>';
        }
      } else {
        if (x && y) {
          positionReview.innerHTML = `<p class="-line-reset">Position: <strong>X: ${x}, Y: ${y}</strong></p>`;
        } else {
          positionReview.innerHTML = '<p class="-line-reset -color-gray_dark">No position set</p>';
        }
      }
    } else if (positionReview) {
      positionReview.innerHTML = '<p class="-line-reset -color-gray_dark">No position required for Intransit</p>';
    }
    // Populate details review via helper (keeps edit-only logic separated)
    this.renderDetailsReview();

    // Populate attributes review
    this.populateAttributesReview();
  }

  renderDetailsReview() {
    const detailsReview = document.getElementById('wizard-review-details');
    if (!detailsReview) return;

    // List of form field names to compare (key => label)
    const fieldsToCheck = {
      code: 'Code',
      usage: 'Usage',
      condition: 'Condition',
      status: 'Status',
      project: 'Project',
      room: 'Room',
      purchase_date: 'Purchase Date',
      purchase_price_cents: 'Purchase Price',
      warranty: 'Warranty',
      warranty_info: 'Warranty Info',
      action: 'Action',
      action_date: 'Action Date'
    };

    const changed = [];

    // Helper to read current form value, trying common variants (e.g. item[condition_id])
    const currentValueFor = (key) => {
      const trySelectors = [
        `input[name="item[${key}]"]`,
        `select[name="item[${key}]"]`,
        `input[name="item[${key}_id]"]`,
        `select[name="item[${key}_id]"]`,
        `input[name="item[${key.replace(/_id$/, '')}]"]`,
      ];

      for (let sel of trySelectors) {
        const el = document.querySelector(sel);
        if (!el) continue;
        if (el.tagName === 'SELECT') {
          return el.selectedOptions[0]?.text || el.value || '';
        }
        return el.value || '';
      }

      // As a last resort, try any element with a data attribute that stores the value
      const dataEl = document.querySelector(`[data-item-${key}]`);
      if (dataEl) return dataEl.dataset[`item${key.replace(/\W/g, '')}`] || '';

      return '';
    };

    // Helper to read original value from originalItem or originalDetails, trying _id variants
    const originalValueFor = (key) => {
      const tryKeys = [key, `${key}_id`, key.replace(/_id$/, '')];
      if (this.originalItem) {
        for (let k of tryKeys) {
          if (Object.prototype.hasOwnProperty.call(this.originalItem, k)) {
            const v = this.originalItem[k];
            return (v === null || v === undefined) ? '' : String(v);
          }
        }
      }

      if (this.originalDetails) {
        for (let k of tryKeys) {
          if (Object.prototype.hasOwnProperty.call(this.originalDetails, k)) {
            const v = this.originalDetails[k];
            return (v === null || v === undefined) ? '' : String(v);
          }
        }
      }

      return '';
    };

    Object.keys(fieldsToCheck).forEach((key) => {
      const label = fieldsToCheck[key];
      const cur = String(currentValueFor(key) || '');
      const orig = String(originalValueFor(key) || '');

      // Normalize whitespace for comparison
      const normalize = (v) => v.trim();

      if (this.isEditMode) {
        if (normalize(cur) !== normalize(orig)) {
          if (!(normalize(cur) === '' && normalize(orig) === '')) {
            changed.push({ label, value: cur, from: orig });
          }
        }
      } else {
        if (cur) changed.push({ label, value: cur });
      }
    });

    if (changed.length === 0) {
      detailsReview.innerHTML = '<p class="-line-reset -color-gray_dark">No additional details</p>';
    } else {
      let detailsHtml = '<ul class="-style-list">';
      changed.forEach(d => {
        if (this.isEditMode && d.from !== undefined) {
          detailsHtml += `<li>${d.label}: <strong>${d.value || '(empty)'}</strong> <small style="color:#666;">(was: ${d.from || '(empty)'})</small></li>`;
        } else {
          detailsHtml += `<li>${d.label}: <strong>${d.value}</strong></li>`;
        }
      });
      detailsHtml += '</ul>';
      detailsReview.innerHTML = detailsHtml;
    }
  }

  populateAttributesReview() {
    const attributesReview = document.getElementById('wizard-review-attributes');
    if (!attributesReview) return;

    const attributes = this.preparedAttributes.length > 0 
      ? this.preparedAttributes 
      : this.collectAllAttributes();

    if (attributes.length === 0) {
      attributesReview.innerHTML = '<p class="-line-reset -color-gray_dark">No attributes</p>';
      return;
    }
    // In edit mode, compare against existing_item_attributes hidden field
    let attrsToShow = attributes;
    if (this.isEditMode) {
      const existingAttrs = this.originalItemAttributes || [];
      const existingMap = {};
      existingAttrs.forEach(ea => { existingMap[ea.catalogue_association_id] = ea; });

      attrsToShow = attributes.filter(attr => {
        const existing = existingMap[attr.catalogue_association_id];
        if (!existing) return true; // new attribute

        const newVal = attr.value;
        const oldVal = existing.value;
        const normalize = (v) => {
          if (v === null || v === undefined) return '';
          if (Array.isArray(v)) return JSON.stringify(v);
          if (typeof v === 'object') return JSON.stringify(v);
          return String(v);
        };

        return normalize(newVal) !== normalize(oldVal);
      });
    }

    if (attrsToShow.length === 0) {
      attributesReview.innerHTML = '<p class="-line-reset -color-gray_dark">No attribute changes</p>';
      return;
    }

    let attributesHtml = '<ul class="-style-list">';
    attrsToShow.forEach(attr => {
      let valueDisplay = '';
      if (Array.isArray(attr.value)) {
        if (attr.catalogue_attribute_type === 'colour') {
          valueDisplay = attr.value.map(v => v.name || v.id).join(', ');
        } else {
          valueDisplay = attr.value.join(', ');
        }
      } else if (typeof attr.value === 'object') {
        try { valueDisplay = JSON.stringify(attr.value); } catch (e) { valueDisplay = String(attr.value); }
      } else {
        valueDisplay = attr.value;
      }
      attributesHtml += `<li><strong>${attr.catalogue_attribute_name}</strong>: ${valueDisplay}</li>`;
    });
    attributesHtml += '</ul>';

    attributesReview.innerHTML = attributesHtml;
  }

  // Edit mode step 2 - Location action handlers
  locationActionChanged(event) {
    const action = event.target.value;
    this.editLocationAction = action;
    
    // Hide all containers
    document.getElementById('wizard-edit-move-container').style.display = 'none';
    document.getElementById('wizard-edit-relocate-container').style.display = 'none';
    document.getElementById('wizard-edit-intransit-container').style.display = 'none';
    
    // Reset move instructions visibility
    const instructionsCard = document.getElementById('wizard-move-instructions');
    if (instructionsCard) {
      instructionsCard.style.display = 'block';
    }
    
    if (action === 'move') {
      this.handleMoveAction();
    } else if (action === 'relocate') {
      this.handleRelocateAction();
    } else if (action === 'intransit') {
      this.handleIntransitAction();
    } else {
      // No action selected, reset everything
      this.editLocationAction = null;
    }
    
    this.updateUI();
  }

  handleMoveAction() {
    const container = document.getElementById('wizard-edit-move-container');
    if (container) {
      container.style.display = 'block';
    }
    
    // Show instructions card (will be hidden if no floorplan)
    const instructionsCard = document.getElementById('wizard-move-instructions');
    if (instructionsCard) {
      instructionsCard.style.display = 'block';
    }
    
    // Check if location is intransit or store (no floorplan)
    const mode = this.state.location.mode;
    if (mode === 'transit' || mode === 'store') {
      // For transit/store locations, show message that no floorplan is available
      this.showMoveNoFloorplanMessage();
    } else {
      // Load existing location floorplan for building locations
      if (this.state.location.locationId && this.editLocationAction) {
        this.loadMoveFloorplan();
      }
    }
  }

  showMoveNoFloorplanMessage() {
    // Hide the instructions card for locations without floorplans
    const instructionsCard = document.getElementById('wizard-move-instructions');
    if (instructionsCard) {
      instructionsCard.style.display = 'none';
    }
    
    const container = document.getElementById('wizard-edit-floorplan-container');
    if (container) {
      const locationName = this.state.location.locationData?.absolute_path_name || 
                          this.state.location.locationData?.name || 
                          'this location';
      const locationType = this.state.location.mode === 'transit' ? 'In Transit' : 'Store Me';
      container.innerHTML = `
        <div class="card -color-gray -no-border -padding_2" style="height: 600px; display: flex; align-items: center; justify-content: center;">
          <div style="text-align: center; max-width: 500px;">
            <p class="-size-medium -color-gray_dark -margin-bottom_2" style="font-weight: 500;">
              This EFFC&M™ is currently in a ${locationType} location.
            </p>
            <p class="-size-medium -color-gray_dark -margin-bottom_2">
              ${locationType} locations do not have floorplans, so you cannot move the EFFC&M™ within this location.
            </p>
            <p class="-size-small -color-gray_dark">
              To change the position, please use the "Relocate" option to move this EFFC&M™ to a building location with a floorplan.
            </p>
          </div>
        </div>
      `;
    }
  }

  loadMoveFloorplan() {
    // Use existing location data to load floorplan
    const locationId = this.state.location.locationId;
    const childLocationId = this.state.location.childLocationId;
    
    // Determine which location to use for floorplan
    const floorplanLocationId = childLocationId || locationId;
    
    // Load location details if not already loaded
    if (!this.state.location.locationData) {
      this.loadLocationDetails(locationId).then(() => {
        this.renderMoveFloorplan();
      });
    } else {
      this.renderMoveFloorplan();
    }
  }

  renderMoveFloorplan() {
    const floorplanLocationId = this.state.location.childLocationId || this.state.location.locationId;
    let floorplan = null;
    let population = null;

    const childFp = this.state.location.childLocationObject?.floorplan;
    const parentFp = this.state.location.locationData?.floorplan;

    if (childFp && childFp.url) {
      floorplan = childFp;
      population = this.state.location.population;
    } else if (parentFp && parentFp.url) {
      floorplan = parentFp;
      population = this.state.location.locationData?.population;
    }
    
    if (!floorplan || !floorplan.url) {
      const container = document.getElementById('wizard-edit-floorplan-container');
      if (container) {
        container.innerHTML = '<div class="card -color-gray -no-border -padding_2"><p class="-size-medium -color-gray_dark">No floorplan available for this location.</p></div>';
      }
      return;
    }
    
    const container = document.getElementById('wizard-edit-floorplan-container');
    if (!container) return;
    
    // Get current x/y values
    const xField = this.hasXFieldTarget ? this.xFieldTarget : document.querySelector('input[name="item[x]"]');
    const yField = this.hasYFieldTarget ? this.yFieldTarget : document.querySelector('input[name="item[y]"]');
    const currentX = xField ? xField.value : '';
    const currentY = yField ? yField.value : '';
    
    // Initialize hidden x/y fields with current values if they exist
    if (xField && currentX) {
      xField.value = currentX;
    }
    if (yField && currentY) {
      yField.value = currentY;
    }
    
    // Build items data, ensuring current item is included and highlighted
    let itemsArray = [];
    if (population) {
      try {
        itemsArray = typeof population === 'string' ? JSON.parse(population) : population;
      } catch (e) {
        console.error('Failed to parse population for floorplan:', e);
        itemsArray = [];
      }
    }
    
    const currentItemData = {
      id: this.formTarget.dataset.itemId || null,
      uuid: this.formTarget.dataset.itemUuid || null,
      code: this.formTarget.dataset.itemCode || null,
      x: currentX || null,
      y: currentY || null,
      current: true, // hint for highlighting
      colour: '#ff5722',
      color: '#ff5722',
      highlight: true,
    };
    
    // Remove any existing entries for this item to avoid duplicates
    itemsArray = itemsArray.filter(
      (it) => String(it.id) !== String(currentItemData.id) && String(it.uuid || '') !== String(currentItemData.uuid || '')
    );
    // Only add current item marker if it has coordinates
    if (currentItemData.x && currentItemData.y) {
      itemsArray.push(currentItemData);
    }
    
    const itemsData = JSON.stringify(itemsArray);
    console.log("Rendering move floorplan with items:", itemsData);
    container.innerHTML = `
      <div data-floorplan="" 
           data-view-mode="write" 
           data-type="location" 
           data-model="item"
           data-plan="${floorplan.url}" 
           data-width="${floorplan.width || 0}" 
           data-height="${floorplan.height || 0}" 
           data-items='${itemsData}'
           data-item='${JSON.stringify(currentItemData)}'
           style="height: 600px;">
        <div class="placeholder">
          <div class="placeholder-message">Initializing Floorplan..</div>
        </div>
      </div>
    `;

    // Initialize floorplan component
    setTimeout(() => {
      try {
        this.floorplanInstance = new Floorplan();
        this.floorplanInstance.initialize();
      } catch (e) {
        console.error('Failed to initialize floorplan:', e);
      }
    }, 100);
  }

  handleRelocateAction() {
    const container = document.getElementById('wizard-edit-relocate-container');
    if (container) {
      container.style.display = 'block';
    }
    
    // Load building locations by default
    this.loadLocationsForRelocate('building');
  }

  relocateModeChanged(event) {
    if (event.preventDefault) {
      event.preventDefault();
    }
    
    const mode = event.currentTarget.dataset.mode || event.currentTarget.closest('.relocate-mode-btn')?.dataset.mode || 'building';
    this.relocateMode = mode;
    
    // Update tab states
    document.querySelectorAll('.relocate-mode-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    const clickedTab = event.currentTarget.closest('.relocate-mode-btn') || event.currentTarget;
    clickedTab.classList.add('active');
    
    // Load locations for the selected mode
    this.loadLocationsForRelocate(mode);
    this.updateRelocateUI();
  }

  loadLocationsForRelocate(mode) {
    const type = mode === 'store' ? 'store' : 'building';
    
    axios
      .get(`/locations.json?type=${encodeURIComponent(type)}`)
      .then((response) => {
        const locations = response.data || [];
        this.populateRelocateLocations(mode, locations);
      })
      .catch((error) => {
        console.error('Failed to load locations for relocate:', error);
        this.populateRelocateLocations(mode, []);
      });
  }

  populateRelocateLocations(mode, locations) {
    let select = null;
    
    if (mode === 'store') {
      if (!this.hasRelocateStoreLocationSelectTarget) return;
      select = this.relocateStoreLocationSelectTarget;
    } else {
      if (!this.hasRelocateLocationSelectTarget) return;
      select = this.relocateLocationSelectTarget;
    }
    
    // Clear existing options
    while (select.firstChild) {
      select.removeChild(select.firstChild);
    }
    
    const placeholder = document.createElement('option');
    placeholder.value = '';
    if (mode === 'store') {
      placeholder.textContent = 'Select a StoreMe Location';
    } else {
      placeholder.textContent = 'Select a Location';
    }
    select.appendChild(placeholder);
    
    locations.forEach((loc) => {
      const opt = document.createElement('option');
      opt.value = loc.id;
      opt.textContent = loc.absolute_path_name || loc.name;
      select.appendChild(opt);
    });

    this.ensureSearchableDropdown(select, { placeholder: placeholder.textContent });
  }

  relocateLocationChanged(event) {
    const value = event.target.value;
    const mode = this.relocateMode;
    
    if (!value) {
      this.clearRelocateChildren();
      this.relocateState.locationId = null;
      this.relocateState.childLocationId = null;
      this.relocateState.childLocationObject = null;
      this.relocateState.locationData = null;
      this.relocateState.floorplanLocationId = null;
      this.relocateState.floorplanObject = null;
      this.hideRelocateFloorplan();
      this.updateUI();
      return;
    }
    
    this.relocateState.locationId = value;
    // this.locationIdFieldTarget.value = value;
    
    // Fetch location details to get children
    this.loadRelocateLocationDetails(value);
  }

  loadRelocateLocationDetails(locationId) {
    return axios
      .get(`/locations/${encodeURIComponent(locationId)}.json`)
      .then((response) => {
        const locationData = response.data.location || {};
        this.relocateState.locationData = locationData;
        // this.state.location.locationData = locationData;
        // Check for children and populate children dropdown
        if (locationData.children && locationData.children.length > 0) {
          this.populateRelocateChildren(locationData);
        } else {
          this.clearRelocateChildren();
        }
        
        this.updateUI();
        return locationData;
      })
      .catch((error) => {
        console.error('Failed to load relocate location details:', error);
        this.clearRelocateChildren();
        throw error;
      });
  }

  populateRelocateChildren(data, level = 1, append = false) {
    const children = data.children;
    console.log("data<><><><><>", data.floorplan);
    if (data.floorplan && data.floorplan.url && this.relocateMode === 'building') { // Only load floorplan for building mode
      this.relocateState.floorplanLocationId = data.id;
      this.relocateState.floorplanObject = data.floorplan;
      this.loadRelocateChildLocationPopulation(data.id);
      this.renderRelocateFloorplan();
    }

    const container = document.getElementById('wizard-relocate-children-container');
    if (!container) return;
    // Normalize children to array
    let items = [];
    if (!children) items = [];
    else if (Array.isArray(children)) items = children;
    else if (typeof children === 'object') items = [children];

    // If not appending at level 1, clear existing selects
    if (!append && level === 1) {
      const selects = Array.from(container.querySelectorAll('select[data-level]'));
      selects.forEach((s) => {
        this.destroySearchableDropdown(s);
        s.remove();
      });
      container.style.display = items.length > 0 ? 'block' : 'none';
    } else {
      container.style.display = 'block';
    }

    // If level 1 and there's an existing Stimulus-targeted select, reuse it
    let select = null;
    if (level === 1 && this.hasRelocateChildLocationSelectTarget) {
      select = this.relocateChildLocationSelectTarget;
      if (select.parentElement !== container) {
        container.appendChild(select);
      }
    }

    // Create a new select for deeper levels (or if no target available)
    if (!select) {
      select = document.createElement('select');
      select.className = 'form-control select';
      container.appendChild(select);
    }

    // Set dataset level to help handlers
    select.dataset.level = String(level);

    // Clear existing options
    while (select.firstChild) select.removeChild(select.firstChild);

    // Placeholder option
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select a Sub Location';
    select.appendChild(placeholder);

    // Add items
    items.forEach((child) => {
      const option = document.createElement('option');
      option.value = child.id;
      option.textContent = child.absolute_path_name || child.name || '';
      select.appendChild(option);
    });

    this.ensureSearchableDropdown(select, { placeholder: 'Select a Sub Location' });

    // Attach change listener only once
    const hasStimulusAction = select.getAttribute && select.getAttribute('data-action');
    if (!hasStimulusAction && !select._relocateChangeBound) {
      select.addEventListener('change', (e) => this.relocateChildLocationChanged(e));
      select._relocateChangeBound = true;
    }
  }

  clearRelocateChildren() {
    const container = document.getElementById('wizard-relocate-children-container');
    if (container) {
      // Remove dynamically created selects
      const selects = Array.from(container.querySelectorAll('select[data-level]'));
      selects.forEach((s) => {
        this.destroySearchableDropdown(s);
        s.remove();
      });
      container.style.display = 'none';
    }
    if (this.hasRelocateChildLocationSelectTarget) {
      try { this.relocateChildLocationSelectTarget.value = ''; } catch (e) {}
    }
    this.relocateState.childLocationId = null;
    this.relocateState.childLocationObject = null;
  }

  relocateChildLocationChanged(event) {
    const select = event.target;
    const level = Number(select.dataset.level) || 1;
    const value = select.value || null;
    const mode = this.relocateMode;

    // Helper to remove deeper selects
    const container = document.getElementById('wizard-relocate-children-container');
    const removeDeeper = (fromLevel) => {
      if (!container) return;
      const selects = Array.from(container.querySelectorAll('select[data-level]'));
      selects.forEach((s) => {
        const l = Number(s.dataset.level) || 0;
        if (l >= fromLevel) {
          this.destroySearchableDropdown(s);
          s.remove();
        }
      });
    };

    removeDeeper(level + 1);

    if (!value) {
      this.relocateState.childLocationId = null;
      this.relocateState.childLocationObject = null;
      this.relocateState.floorplanLocationId = null;
      this.relocateState.floorplanObject = null;
      // Restore hidden location_id to parent
      if (this.hasLocationIdFieldTarget) {
        if (level === 1) {
          this.locationIdFieldTarget.value = this.relocateState.locationId || '';
        } else if (container) {
          const prev = container.querySelector(`select[data-level="${level - 1}"]`);
          this.locationIdFieldTarget.value = prev ? (prev.value || this.relocateState.locationId || '') : (this.relocateState.locationId || '');
        }
      }
      this.hideRelocateFloorplan();
      this.updateUI();
      return;
    }

    // Always update location_id to current selection
    if (this.hasLocationIdFieldTarget) {
      this.locationIdFieldTarget.value = value;
    }

    // Update state
    this.relocateState.childLocationId = value;
    this.relocateState.childLocationObject = null;

    // Fetch selected child details to check for further children
    axios.get(`/locations/${encodeURIComponent(value)}.json`)
      .then((response) => {
        const data = response.data.location || {};
        this.relocateState.childLocationObject = data;
        this.state.location.childLocationObject = data;
        // Check if this child has more children
        if (data.children && data.children.length > 0) {
          // Has more children - populate next level dropdown
          if (data.floorplan && data.floorplan.url && mode === 'building') { // Only load floorplan for building mode
            this.relocateState.floorplanLocationId = data.id;
            this.relocateState.floorplanObject = data.floorplan;
            this.loadRelocateChildLocationPopulation(data.id);
            this.renderRelocateFloorplan();
          }

          this.populateRelocateChildren(data, level + 1, true);

        } else {
          // This is the deepest child - set floorplan/population
          if (data.floorplan && data.floorplan.url && mode === 'building') { // Only load floorplan for building mode
            this.relocateState.floorplanLocationId = data.id;
            this.relocateState.floorplanObject = data.floorplan;
            this.loadRelocateChildLocationPopulation(data.id);
            this.renderRelocateFloorplan();
          } else if (mode === 'store') {
            this.relocateState.floorplanLocationId = null;
            this.relocateState.floorplanObject = null;
            this.hideRelocateFloorplan();
          } else {
            this.relocateState.floorplanLocationId = null;
            this.relocateState.floorplanObject = null;
            this.hideRelocateFloorplan();
          }
        }

        this.updateUI();
      })
      .catch((error) => {
        console.error('Failed to load relocate child location details:', error);
      });

    this.updateUI();
  }

  loadRelocateChildLocationPopulation(childLocationId) {
    axios
      .get(`/locations/${encodeURIComponent(childLocationId)}.json`)
      .then((response) => {
        const childLocationData = response.data.location || {};
        this.relocateState.population = childLocationData.population || null;
      })
      .catch((error) => {
        console.error('Failed to load relocate child location population:', error);
        this.relocateState.population = null;
      });
  }

  renderRelocateFloorplan() {
    const floorplan = this.relocateState.floorplanObject;
    const floorplanLocationId = this.relocateState.floorplanLocationId;
    const population = this.relocateState.population;
    
    if (!floorplan || !floorplan.url) {
      this.hideRelocateFloorplan();
      return;
    }
    
    const container = document.getElementById('wizard-relocate-floorplan-inner');
    if (!container) return;
    
    const containerParent = document.getElementById('wizard-relocate-floorplan-container');
    if (containerParent) {
      containerParent.style.display = 'block';
    }
    
    // Clear x/y fields for new location
    const xField = this.hasXFieldTarget ? this.xFieldTarget : document.querySelector('input[name="item[x]"]');
    const yField = this.hasYFieldTarget ? this.yFieldTarget : document.querySelector('input[name="item[y]"]');
    if (xField) xField.value = '';
    if (yField) yField.value = '';
    
    let itemsData = '[]';
    if (population) {
      if (typeof population === 'string') {
        itemsData = population;
      } else {
        itemsData = JSON.stringify(population);
      }
    }
    
    const currentItemData = '{}';
    
    container.innerHTML = `
      <div data-floorplan="" 
           data-view-mode="write" 
           data-type="location" 
           data-model="item"
           data-plan="${floorplan.url}" 
           data-width="${floorplan.width || 0}" 
           data-height="${floorplan.height || 0}" 
           data-items='${itemsData}'
           data-item='${currentItemData}'
           style="height: 600px;">
        <div class="placeholder">
          <div class="placeholder-message">Initializing Floorplan..</div>
        </div>
      </div>
    `;

    setTimeout(() => {
      try {
        this.floorplanInstance = new Floorplan();
        this.floorplanInstance.initialize();
      } catch (e) {
        console.error('Failed to initialize relocate floorplan:', e);
      }
    }, 100);
  }

  hideRelocateFloorplan() {
    const container = document.getElementById('wizard-relocate-floorplan-container');
    if (container) {
      container.style.display = 'none';
    }
  }

  updateRelocateUI() {
    const mode = this.relocateMode;
    
    document.querySelectorAll('.relocate-mode-dropdown').forEach(dropdown => {
      if (dropdown.dataset.mode === mode) {
        dropdown.style.display = 'block';
      } else {
        dropdown.style.display = 'none';
      }
    });
  }

  handleIntransitAction() {
    const container = document.getElementById('wizard-edit-intransit-container');
    if (container) {
      container.style.display = 'block';
    }
    
    // Load transit locations
    this.loadLocationsForIntransit();
  }

  loadLocationsForIntransit() {
    // Use the same endpoint format that works for other location types
    // The regular /locations.json endpoint handles transit locations
    axios
      .get(`/locations.json?type=transit`)
      .then((response) => {
        const locations = response.data || [];
        this.populateIntransitLocations(locations);
      })
      .catch((error) => {
        console.error('Failed to load transit locations:', error);
        this.populateIntransitLocations([]);
      });
  }

  populateIntransitLocations(locations) {
    if (!this.hasIntransitLocationSelectTarget) return;
    const select = this.intransitLocationSelectTarget;
    
    while (select.firstChild) {
      select.removeChild(select.firstChild);
    }
    
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select a Transit Location';
    select.appendChild(placeholder);
    
    locations.forEach((loc) => {
      const opt = document.createElement('option');
      // API v8 uses uuid as identifier, but we need id for the form
      // Try to get id from the location object, or use uuid if id is not available
      opt.value = loc.id || loc.uuid || '';
      // Use absolute_path_name or name, with override_name format if available
      const displayName = loc.absolute_path_name || loc.name || '';
      opt.textContent = displayName;
      opt.dataset.uuid = loc.uuid || '';
      select.appendChild(opt);
    });

    this.ensureSearchableDropdown(select, { placeholder: placeholder.textContent });
  }

  intransitLocationChanged(event) {
    const value = event.target.value;
    
    if (!value) {
      this.clearIntransitChildren();
      this.intransitState.locationId = null;
      this.intransitState.childLocationId = null;
      this.intransitState.childLocationObject = null;
      this.intransitState.locationData = null;
      this.updateUI();
      return;
    }
    
    this.intransitState.locationId = value;
    
    // Fetch location details to get children
    this.loadIntransitLocationDetails(value);
  }

  loadIntransitLocationDetails(locationId) {
    // Get existing location UUID for filtering child locations in transit
    // In edit mode, use the current item's location UUID (could be child or parent)
    let existingLocationUuid = null;
    if (this.isEditMode) {
      // Try to get UUID from child location first (if item is in a child location)
      if (this.state.location.childLocationObject && this.state.location.childLocationObject.uuid) {
        existingLocationUuid = this.state.location.childLocationObject.uuid;
      } else if (this.state.location.locationData && this.state.location.locationData.uuid) {
        // Fallback to parent location UUID
        existingLocationUuid = this.state.location.locationData.uuid;
      } else if (this.state.location.locationId) {
        existingLocationUuid = this.state.location.locationId;
      }
    }
    
    // Build URL with existing_location_uuid parameter if available
    let url = `/locations/${encodeURIComponent(locationId)}.json`;
    if (existingLocationUuid) {
      url += `?existing_location_uuid=${encodeURIComponent(existingLocationUuid)}`;
    }
    
    return axios
      .get(url)
      .then((response) => {
        const locationData = response.data.location || {};
        this.intransitState.locationData = locationData;
        
        // Check for children and populate children dropdown
        if (locationData.children && locationData.children.length > 0) {
          // For transit locations, show all children (no floorplan filtering needed)
          this.populateIntransitChildren(locationData.children);
        } else {
          this.clearIntransitChildren();
        }
        
        this.updateUI();
        return locationData;
      })
      .catch((error) => {
        console.error('Failed to load intransit location details:', error);
        // For transit locations, it's okay if details fail - they might not have children
        // Just clear children and continue
        this.clearIntransitChildren();
        // Set basic location data from what we have
        this.intransitState.locationData = {
          id: locationId,
          transit: true
        };
        this.updateUI();
        // Don't throw error - allow user to continue
        return {};
      });
  }

  populateIntransitChildren(children, level = 1, append = false) {
    const container = document.getElementById('wizard-intransit-children-container');
    if (!container) return;

    // Normalize children to array
    let items = [];
    if (!children) items = [];
    else if (Array.isArray(children)) items = children;
    else if (typeof children === 'object') items = [children];

    // If not appending at level 1, clear existing selects
    if (!append && level === 1) {
      const selects = Array.from(container.querySelectorAll('select[data-level]'));
      selects.forEach((s) => {
        this.destroySearchableDropdown(s);
        s.remove();
      });
      container.style.display = items.length > 0 ? 'block' : 'none';
    } else {
      container.style.display = 'block';
    }

    // If level 1 and there's an existing Stimulus-targeted select, reuse it
    let select = null;
    if (level === 1 && this.hasIntransitChildLocationSelectTarget) {
      select = this.intransitChildLocationSelectTarget;
      if (select.parentElement !== container) {
        container.appendChild(select);
      }
    }

    // Create a new select for deeper levels (or if no target available)
    if (!select) {
      select = document.createElement('select');
      select.className = 'form-control select';
      container.appendChild(select);
    }

    // Set dataset level to help handlers
    select.dataset.level = String(level);

    // Clear existing options
    while (select.firstChild) select.removeChild(select.firstChild);

    // Placeholder option
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select a Sub Location';
    select.appendChild(placeholder);

    // Add items
    items.forEach((child) => {
      const option = document.createElement('option');
      option.value = child.id;
      option.textContent = child.absolute_path_name || child.name || '';
      select.appendChild(option);
    });

    this.ensureSearchableDropdown(select, { placeholder: 'Select a Sub Location' });

    // Attach change listener only once
    const hasStimulusAction = select.getAttribute && select.getAttribute('data-action');
    if (!hasStimulusAction && !select._intransitChangeBound) {
      select.addEventListener('change', (e) => this.intransitChildLocationChanged(e));
      select._intransitChangeBound = true;
    }
  }

  clearIntransitChildren() {
    const container = document.getElementById('wizard-intransit-children-container');
    if (container) {
      // Remove dynamically created selects
      const selects = Array.from(container.querySelectorAll('select[data-level]'));
      selects.forEach((s) => {
        this.destroySearchableDropdown(s);
        s.remove();
      });
      container.style.display = 'none';
    }
    if (this.hasIntransitChildLocationSelectTarget) {
      try { this.intransitChildLocationSelectTarget.value = ''; } catch (e) {}
    }
    this.intransitState.childLocationId = null;
    this.intransitState.childLocationObject = null;
  }

  intransitChildLocationChanged(event) {
    const select = event.target;
    const level = Number(select.dataset.level) || 1;
    const value = select.value || null;

    // Helper to remove deeper selects
    const container = document.getElementById('wizard-intransit-children-container');
    const removeDeeper = (fromLevel) => {
      if (!container) return;
      const selects = Array.from(container.querySelectorAll('select[data-level]'));
      selects.forEach((s) => {
        const l = Number(s.dataset.level) || 0;
        if (l >= fromLevel) {
          this.destroySearchableDropdown(s);
          s.remove();
        }
      });
    };

    removeDeeper(level + 1);

    if (!value) {
      this.intransitState.childLocationId = null;
      this.intransitState.childLocationObject = null;
      // Restore hidden location_id to parent
      if (this.hasLocationIdFieldTarget) {
        if (level === 1) {
          this.locationIdFieldTarget.value = this.intransitState.locationId || (this.state.location.locationId || '');
        } else if (container) {
          const prev = container.querySelector(`select[data-level="${level - 1}"]`);
          this.locationIdFieldTarget.value = prev ? (prev.value || this.intransitState.locationId || '') : (this.intransitState.locationId || '');
        }
      }
      this.updateUI();
      return;
    }

    // Always update location_id to current selection
    if (this.hasLocationIdFieldTarget) {
      this.locationIdFieldTarget.value = value;
    }

    // Update state
    this.intransitState.childLocationId = value;
    this.intransitState.childLocationObject = null;

    // Fetch selected child details to check for further children
    axios.get(`/locations/${encodeURIComponent(value)}.json`)
      .then((response) => {
        const data = response.data.location || {};
        this.intransitState.childLocationObject = data;
        this.state.location.childLocationObject = data;

        // Check if this child has more children
        if (data.children && data.children.length > 0) {
          // Has more children - populate next level dropdown
          this.populateIntransitChildren(data.children, level + 1, true);
        }
        // For intransit, no floorplan handling needed

        this.updateUI();
      })
      .catch((error) => {
        console.error('Failed to load intransit child location details:', error);
      });

    this.updateUI();
  }

  canGoNext() {
    // For step 1 in create mode, check code validation
    if (this.currentStep === 1 && !this.isEditMode) {
      const codeField = document.querySelector('input[name="item[code]"]');
      const code = codeField?.value.trim();

      if (this.bulkCreateMode) {
        const quantity = document.querySelector('input[name="bulk[quantity]"]')?.value?.trim();
        if (!quantity) {
          return false;
        }
      } else if ((!code || !this.isCodeValid)) {
        return false;
      }

      const catalogueItemId = document.querySelector('input[name="item[catalogue_item_id]"]')?.value?.trim();
      if (!catalogueItemId) {
        return false;
      }

      if (this.bulkCreateMode) {
        const bulkRoot = document.querySelector('[data-controller*="items--wizard-bulk"]');
        return bulkRoot && bulkRoot.dataset.bulkReady === 'true';
      }
    }
    // Step 2 create: x/y when floorplan is active (building or store child with plan).
    // Transit has no floorplan; require parent + sub-location only (same as validator).
    if (this.currentStep === 2 && !this.isEditMode) {
      const mode = this.state.location.mode;
      const needsPosition = (mode === 'building') && this.state.location.floorplanObject?.url; // Only load floorplan for building mode validation
      if (needsPosition) {
        if (this.bulkCreateMode) return true;
        const x = this.hasXFieldTarget ? this.xFieldTarget.value : '';
        const y = this.hasYFieldTarget ? this.yFieldTarget.value : '';
        return x && y && x.trim() !== '' && y.trim() !== '';
      }
      if (mode === 'store') {
        return !!(this.state.location.locationId && this.state.location.childLocationId);
      }
      if (mode === 'transit') {
        return !!(this.state.location.locationId && this.state.location.childLocationId);
      }
      return false;
    }
    
    // For step 1 in edit mode, always allow proceeding (validation happens in next())
    if (this.currentStep === 1 && this.isEditMode) {
      return true;
    }
    
    // For step 2 in edit mode, check if action is selected and validated
    if (this.currentStep === 2 && this.isEditMode) {
      if (!this.editLocationAction) {
        return true;
      }
      
      if (this.editLocationAction === 'move') {
        // For move action, check if location has floorplan
        const mode = this.state.location.mode;
        if (mode === 'building') {
          // Building locations require x/y coordinates
          const xField = document.querySelector('input[name="item[x]"]');
          const yField = document.querySelector('input[name="item[y]"]');
          const x = xField ? xField.value : '';
          const y = yField ? yField.value : '';
          return x && y && x.trim() !== '' && y.trim() !== '';
        } else {
          // Transit/store locations don't have floorplans, so no x/y required
          return true;
        }
      } else if (this.editLocationAction === 'relocate') {
        // If no new location is selected, allow proceeding (will use existing location)
        if (!this.relocateState.locationId) {
          return true;
        }
        // If floorplan was shown, x/y must be set
        if (this.relocateState.floorplanObject) {
          const xField = document.querySelector('input[name="item[x]"]');
          const yField = document.querySelector('input[name="item[y]"]');
          const x = xField ? xField.value : '';
          const y = yField ? yField.value : '';
          return x && y && x.trim() !== '' && y.trim() !== '';
        }
        return true;
      } else if (this.editLocationAction === 'intransit') {
        // If no new location is selected, allow proceeding (will use existing location)
        if (!this.intransitState.locationId) {
          return true;
        }
        return true;
      }
      
      return false;
    }
    
    // For step 1 (create mode), validation will happen when Next button is clicked
    // This method is called frequently during UI updates, so we don't want to show errors here
    return true;
  }

  updateUI() {
    this.element.querySelectorAll('.wizard-step').forEach((stepEl) => {
      const step = parseInt(stepEl.dataset.step, 10);
      const shouldShow = step === this.currentStep;
      if (shouldShow) {
        stepEl.classList.remove('-state-hidden');
      } else {
        stepEl.classList.add('-state-hidden');
      }
    });

    this.element.querySelectorAll('.wizard-step-number').forEach((el) => {
      const step = parseInt(el.dataset.step, 10);
      const isActive = step === this.currentStep;
      const isComplete = step < this.currentStep;

      if (isActive) {
        el.classList.remove('-color-gray_light');
        el.classList.add('-color-blue');
      } else if (isComplete) {
        el.classList.remove('-color-gray_light', '-color-blue');
        el.classList.add('-color-asset');
      } else {
        el.classList.remove('-color-blue', '-color-asset');
        el.classList.add('-color-gray_light');
      }
    });

    this.element.querySelectorAll('.wizard-progress-line-inner').forEach((el) => {
      const step = parseInt(el.dataset.step, 10);
      if (step < this.currentStep) {
        el.style.width = '100%';
        el.style.backgroundColor = '#6DA9ED';
      } else {
        el.style.width = '0%';
        el.style.backgroundColor = '#e0e0e0';
      }
    });

    this.element.querySelectorAll('[data-step-label]').forEach((el) => {
      const step = parseInt(el.dataset.stepLabel, 10);
      if (step === this.currentStep) {
        el.classList.remove('-color-gray_dark');
        el.classList.add('-color-black');
      } else {
        el.classList.remove('-color-black');
        el.classList.add('-color-gray_dark');
      }
    });

    if (this.hasBackButtonTarget) {
      this.backButtonTarget.classList.toggle(
        '-state-hidden',
        this.currentStep === 1
      );
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle('-state-hidden', this.currentStep === this.maxStep);
      const onIntermediateStep = this.currentStep < this.maxStep;
      this.nextButtonTarget.disabled = onIntermediateStep && !this.canGoNext();
    }

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.toggle(
        '-state-hidden',
        this.currentStep !== this.maxStep
      );
    }

    this.updateBulkPlacementUI();
  }
}

